import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@iconify/react'
import { Input, Spin, Tooltip, message } from 'antd'
import dayjs from 'dayjs'
import {
    DrawingEditor,
    SwitchUserModal,
    ToastContainer,
    ToastProvider,
    UserProvider,
    createDrawing,
    listActivityByDrawing,
    listVersionsByDrawing,
    getHtmlScreenSignature,
    isTextValueControl,
    loadDrawingState,
    saveVersion,
} from '@scopmx/markup-editor'
import '@scopmx/markup-editor/styles.css'

import {
    showActionNotification,
    // NOTIFICATION_Z_ABOVE_FULLSCREEN,
} from '../../../components/notification'
import DeletePopUp from '../../../components/deletePopUp/deletePopUp'
import {
    // activatePrototypeVersion,
    addPrototypeComment,
    addPrototypeReply,
    deletePrototypeComment,
    deletePrototypeReply,
    exportPrototypeComments,
    updatePrototypeComment,
    getPrototypeDetail,
    listPrototypeActivity,
    listPrototypeComments,
    listPrototypeVersions,
    resolvePrototypeComment,
    uploadPrototypeHtmlFile,
    uploadPrototypeVersion,
} from '../prototype.service'
import {
    clearCachedDrawingIdEverywhere,
    persistCachedDrawingId,
    resolveCachedDrawingId,
} from '../prototypeDrawingCache'
import { restoreHtmlScreenRoutes } from '../prototypeHtmlScreenRoutes'
import { screenIdForIframe } from '../prototypeHtmlFingerprint'
import {
    toPackageCommentRows,
    toPackageCommentScreen,
    toPackageSnapshot,
} from '../prototypeDrawingStorage'
import {
    PROTOTYPE_TAB_BAR_HOST_ATTR,
    PROTOTYPE_TAB_BAR_SLOT_ATTR,
    attachTabBarSlot,
    removeTabBarHost,
} from './projectTabBarPortal'
import styles from './ProjectPrototype.module.css'

const { TextArea } = Input

/** Host-owned Comments tab (replaces package Comments + Threads). */
const HOST_COMMENTS_TAB = 'comments'

/**
 * Ceiling on the recorded click path handed to the package as `screen.chain`.
 *
 * Matches the package's own MAX_HOST_PATH_STEPS. The path is only replayable
 * because it starts at the LOAD screen, so it is truncated from the END —
 * dropping its first steps would leave a sequence rooted nowhere, which is the
 * exact state that makes cross-screen redirect fail.
 *
 * Sized for prototypes that reach a screen through popups and dropdowns rather
 * than a flat menu: those record a step per interaction, not per screen, so a
 * genuinely nested flow can run to a few dozen. Replay costs roughly 0.2s for a
 * step that opens a menu and 0.4s for one that changes screen, so the ceiling is
 * also the worst-case wait.
 */
const MAX_HOST_CHAIN_STEPS = 40

/**
 * How long after a click a screen change still counts as that click's doing.
 *
 * The watcher samples every 250ms, so a real transition is credited well inside
 * this. Anything slower is a prototype advancing itself, and recording it as a
 * click step would have replay fire the trigger at a moment the prototype is not
 * ready for.
 */
const CLICK_ATTRIBUTION_MS = 1500

/**
 * How long to give a click to change the screen before concluding it did not.
 *
 * Must clear a couple of sample intervals, or a transition still in flight gets
 * filed as a no-op click and the screen it lands on is then credited to nobody.
 */
const CLICK_SETTLE_MS = 700

/**
 * Stamped onto every recorded screen so a payload says which build produced it.
 *
 * Three separate rounds of debugging were spent on data that turned out to have
 * been recorded by a stale bundle, and there was no way to tell from the
 * payload alone — a missing field looks identical whether the code that writes
 * it is absent or merely not running yet. This makes that visible for free:
 * anyone reading a comment row can see the recorder that wrote it.
 *
 * Bump it whenever the recorded shape changes.
 */
const RECORDER_VERSION = 'r7-landsOn+text-values'

const COMMENT_SHAPE_COLOR = '#000000'
const TEXT_LABEL_FILL = '#111827'

const EMPTY_SNAPSHOT = {
    canvasWidth: 1440,
    canvasHeight: 900,
    comments: [],
    annotations: { version: '5', objects: [] },
}

/** Screen fields for the comments API (hash + how this screen was reached). */
const serializeHtmlTrigger = (trigger) => {
    if (!trigger || typeof trigger !== 'object') return null
    const path = String(trigger.path || '').trim()
    const text = String(trigger.text || '').trim()
    const tag = String(trigger.tag || '').trim()
    if (!path && !(tag && text)) return null
    // `!== undefined`, not "is non-empty". An empty string is a real select
    // value — it is what a placeholder option ("Relationship — optional")
    // carries — and dropping it costs more than the field is worth: the package
    // treats a trigger WITH a value as one to drive by writing the value and
    // firing `change`, and a trigger WITHOUT one as something to click. Clicking
    // a <select> navigates nowhere, so a select whose value was blanked out here
    // replays as a guaranteed no-op, and any screen behind it is unreachable.
    const value = trigger.value != null ? String(trigger.value) : undefined
    const checked =
        typeof trigger.checked === 'boolean' ? trigger.checked : undefined
    // The screen this step lands on. The package uses it to check each step the
    // moment it fires — retrying one that did not take, skipping one the
    // prototype has already satisfied — instead of firing the whole chain blind
    // and only discovering at the end that step two never happened.
    const landsOn =
        typeof trigger.landsOn === 'string' && trigger.landsOn.trim()
            ? trigger.landsOn.trim()
            : undefined
    return {
        ...(path ? { path } : {}),
        ...(text ? { text } : {}),
        ...(tag ? { tag } : {}),
        ...(value !== undefined ? { value } : {}),
        ...(checked !== undefined ? { checked } : {}),
        ...(landsOn ? { landsOn } : {}),
    }
}

const serializeHtmlChain = (chain, trigger) => {
    const steps = (Array.isArray(chain) ? chain : [])
        .map(serializeHtmlTrigger)
        .filter(Boolean)
    const last = serializeHtmlTrigger(trigger)
    if (last) {
        // Identity is the control, not where it landed: the same button appears
        // once in a path even if the recorder saw it settle twice.
        const key = `${last.path || ''}|${last.tag || ''}|${last.text || ''}`
        const exists = steps.some(
            (step) =>
                `${step.path || ''}|${step.tag || ''}|${step.text || ''}` === key
        )
        if (!exists) steps.push(last)
    }
    return steps
}

const toApiCommentScreen = (screen) => {
    if (!screen || typeof screen !== 'object') return null
    const hash =
        typeof screen.hash === 'string' && screen.hash.trim()
            ? screen.hash.trim()
            : undefined
    const visibleId =
        hash ||
        (Array.isArray(screen.visibleIds) ? String(screen.visibleIds[0] || '') : '')
    // NEVER fall back to `htmlAnchor`. That is the element the MARK sits on,
    // on the DESTINATION screen — a heading, a card, a span. It serializes to
    // a perfectly valid-looking trigger whose path resolves to nothing on the
    // screen the walk starts from, so replay is a guaranteed no-op, and the
    // package keeps the first trigger it ever saw, so the bad one is never
    // displaced. No trigger at all is strictly better: the package then falls
    // back to the recorded chain or a reload.
    const trigger = serializeHtmlTrigger(screen.trigger)
    const chain = serializeHtmlChain(screen.chain, trigger)
    const from =
        typeof screen.from === 'string' && screen.from.trim()
            ? screen.from.trim()
            : null
    // What the screen looked like structurally, with none of its data. The
    // exact hash covers every visible character, so a list gaining a row or a
    // badge ticking mints a new id for the same screen and redirect reports a
    // failure while the prototype sits on exactly the right page. Measured on
    // the real prototype: 2 of 10 deep routes failed that way, and every one of
    // them had a byte-identical structure.
    const signature =
        typeof screen.signature === 'string' && screen.signature.trim()
            ? screen.signature.trim()
            : undefined
    return {
        ...(hash ? { hash } : {}),
        recorder: RECORDER_VERSION,
        ...(signature ? { signature } : {}),
        visibleIds: Array.isArray(screen.visibleIds)
            ? screen.visibleIds.filter(Boolean)
            : visibleId
              ? [visibleId]
              : [],
        label: screen.label || visibleId || undefined,
        scrollY: Number(screen.scrollY) || 0,
        trigger: trigger || null,
        chain,
        from,
        // The frozen DOM of this screen, from the editor's
        // captureScreenSnapshotForComment(). It is the only route back that
        // does not depend on replaying somebody else's clicks, and the only one
        // that works for a teammate who was never on this screen.
        //
        // Omitted rather than sent null when absent, so an update that does not
        // recapture cannot wipe a snapshot the create already stored.
        ...(typeof screen.snapshot === 'string' && screen.snapshot
            ? { snapshot: screen.snapshot }
            : {}),
    }
}

const normalizeApiCommentScreen = (apiComment) => {
    const screen = apiComment?.screen && typeof apiComment.screen === 'object'
        ? apiComment.screen
        : {}
    return toApiCommentScreen(screen)
}

/** Map Prototype API reply → panel reply. */
const resolveAuthorId = (item) => {
    if (!item) return null
    const raw =
        item.authorId ||
        item.createdBy?._id ||
        item.createdBy?.id ||
        item.createdBy ||
        item.userId ||
        null
    if (!raw || typeof raw === 'object') return null
    return String(raw)
}

const isSameUser = (user, item) => {
    const currentId = String(user?.id || '')
    const authorId = String(item?.authorId || '')
    if (currentId && authorId && currentId === authorId) return true
    const currentName = String(user?.name || '').trim().toLowerCase()
    const authorName = String(item?.author || '').trim().toLowerCase()
    return Boolean(currentName && authorName && currentName === authorName)
}

const toEditorReply = (apiReply, parent) => {
    const id = String(apiReply?._id || '')
    if (!id) return null
    return {
        id,
        parentId: parent?.id || null,
        annotationId: parent?.annotationId || null,
        authorId: resolveAuthorId(apiReply),
        author: apiReply.authorName || apiReply.createdByName || 'User',
        text: apiReply.body || '',
        createdAt: apiReply.createdAt || new Date().toISOString(),
        updatedAt: apiReply.updatedAt || undefined,
    }
}

/** Map Prototype API comment → DrawingEditor / host panel comment. */
const toEditorComment = (apiComment) => {
    const id = String(apiComment?._id || '')
    if (!id) return null
    const annotationId = String(
        apiComment.annotationId ||
            apiComment.shape?.annotationId ||
            id
    )
    const parent = { id, annotationId }
    const replies = Array.isArray(apiComment.replies)
        ? apiComment.replies.map((item) => toEditorReply(item, parent)).filter(Boolean)
        : []
    return {
        id,
        annotationId: String(
            apiComment.annotationId ||
                apiComment.shape?.annotationId ||
                id
        ),
        authorId: resolveAuthorId(apiComment),
        author: apiComment.authorName || apiComment.createdByName || 'User',
        text: apiComment.body || '',
        createdAt: apiComment.createdAt || new Date().toISOString(),
        updatedAt: apiComment.updatedAt || undefined,
        elementLabel:
            apiComment.shapeLabel ||
            apiComment.screen?.label ||
            undefined,
        isResolved: Boolean(apiComment.isResolved),
        isOrphaned: Boolean(apiComment.isOrphaned),
        replyCount: apiComment.replyCount ?? replies.length,
        replies,
        versionId: apiComment.versionId
            ? String(apiComment.versionId)
            : null,
        shape: apiComment.shape || null,
        ...(() => {
            const screen = toPackageCommentScreen(
                normalizeApiCommentScreen(apiComment)
            )
            return screen
                ? { screen, htmlScreen: screen }
                : { screen: null, htmlScreen: null }
        })(),
    }
}

const sortCommentsNewestFirst = (list) =>
    [...(Array.isArray(list) ? list : [])].sort((a, b) => {
        const aTime = new Date(a?.createdAt || 0).getTime()
        const bTime = new Date(b?.createdAt || 0).getTime()
        return bTime - aTime
    })

const SHAPE_KIND_ALIASES = {
    'i-text': 'text',
    itext: 'text',
    label: 'text',
    revisioncloud: 'cloud',
    cloudpolygon: 'cloud',
    'dashed-rect': 'dashedrect',
    'rounded-rect': 'roundedrect',
    'double-arrow': 'doublearrow',
}

const COMMENT_BUBBLE_PATH = 'M7.9 20A9 9 0 1 0 4 16.1L2 22Z'

const STAMP_STYLES = {
    stampcheck: { glyph: '✓', fill: '#16a34a' },
    stampwarning: { glyph: '!', fill: '#d97706' },
    stampstop: { glyph: '✕', fill: '#dc2626' },
}

const normalizeShapeKind = (shape, fallbackType = '') => {
    const raw = String(
        shape?.kind || shape?.toolId || fallbackType || 'rect'
    ).toLowerCase()
    return SHAPE_KIND_ALIASES[raw] || raw
}

const boxPoints = (x, y, w, h) => [
    { x, y },
    { x: x + w, y },
    { x: x + w, y: y + h },
    { x, y: y + h },
]

/** Scalloped cloud outline so revive does not need RevisionCloud. */
const cloudPathFromBox = (x, y, w, h) => {
    const pts = boxPoints(x, y, w, h)
    const bump = 20
    let d = ''
    for (let i = 0; i < pts.length; i += 1) {
        const a = pts[i]
        const b = pts[(i + 1) % pts.length]
        const dx = b.x - a.x
        const dy = b.y - a.y
        const len = Math.hypot(dx, dy) || 1
        const steps = Math.max(1, Math.ceil(len / bump))
        const sx = dx / steps
        const sy = dy / steps
        const r = Math.max(2, Math.hypot(sx, sy) / 2)
        for (let step = 0; step < steps; step += 1) {
            const x0 = a.x + sx * step
            const y0 = a.y + sy * step
            if (!d) d = `M ${x0} ${y0}`
            d += ` A ${r} ${r} 0 0 1 ${x0 + sx} ${y0 + sy}`
        }
    }
    return `${d} Z`
}

/** Map Prototype API shape → Fabric object JSON for snapshot revive. */
const shapeToFabricObject = (shape, annotationId, screen) => {
    if (!shape || !annotationId) return null
    const color = shape.color || COMMENT_SHAPE_COLOR
    const x = Number(shape.x) || 0
    const y = Number(shape.y) || 0
    const w = Math.max(1, Number(shape.w) || 0)
    const h = Math.max(1, Number(shape.h) || 0)
    const kind = normalizeShapeKind(shape)
    // No default background for shapes: only outlines / strokes.
    // (Keeping text fill because text is drawn as filled glyphs.)
    const fillSoft = null
    const cx = x + w / 2
    const cy = y + h / 2
    const storedPoints = Array.isArray(shape.points)
        ? shape.points
              .map((pt) => ({
                  x: Number(pt?.x) || 0,
                  y: Number(pt?.y) || 0,
              }))
              .filter((pt) => Number.isFinite(pt.x) && Number.isFinite(pt.y))
        : []
    const base = {
        version: '5.3.0',
        originX: shape.originX || 'left',
        originY: shape.originY || 'top',
        left: x,
        top: y,
        fill: fillSoft,
        stroke: color,
        strokeWidth: 2,
        scaleX: 1,
        scaleY: 1,
        angle: 0,
        opacity: 1,
        selectable: true,
        evented: true,
        annotationId,
        id: annotationId,
        toolId: kind,
        ...(screen?.hash ? { htmlScreenId: screen.hash } : {}),
    }
    if (kind === 'text') {
        const fill =
            color && color !== COMMENT_SHAPE_COLOR
                ? color
                : TEXT_LABEL_FILL
        return {
            ...base,
            type: 'i-text',
            text: String(shape.text || '').trim() || 'Label',
            fill,
            fontSize: Math.max(12, Number(shape.fontSize) || 20),
            fontFamily: 'Arial, sans-serif',
            objectCaching: false,
            stroke: null,
            strokeWidth: 0,
            toolId: 'text',
            width: w,
            height: h,
        }
    }
    if (kind === 'ellipse') {
        return {
            ...base,
            type: 'ellipse',
            rx: w / 2,
            ry: h / 2,
            left: cx,
            top: cy,
            originX: 'center',
            originY: 'center',
        }
    }
    if (kind === 'circle') {
        return {
            ...base,
            type: 'circle',
            radius: Math.max(1, Math.min(w, h) / 2),
            left: cx,
            top: cy,
            originX: 'center',
            originY: 'center',
        }
    }
    if (kind === 'pin') {
        const size = 16
        return {
            ...base,
            type: 'circle',
            radius: size / 2,
            left: Number.isFinite(Number(shape.x)) ? x : cx,
            top: Number.isFinite(Number(shape.y)) ? y : cy,
            originX: 'center',
            originY: 'center',
            fill: null,
            stroke: color,
            toolId: 'pin',
        }
    }
    if (kind === 'comment') {
        return {
            ...base,
            type: 'path',
            path: shape.path || COMMENT_BUBBLE_PATH,
            left: cx,
            top: cy,
            originX: 'center',
            originY: 'center',
            scaleX: Number(shape.scaleX) || 1.4,
            scaleY: Number(shape.scaleY) || 1.4,
            fill: null,
            stroke: color,
            strokeWidth: 2,
            toolId: 'comment',
        }
    }
    if (kind === 'cloud') {
        return {
            ...base,
            type: 'path',
            path: shape.path || cloudPathFromBox(x, y, w, h),
            left: x,
            top: y,
            originX: 'left',
            originY: 'top',
            fill: null,
            stroke: color,
            toolId: 'cloud',
        }
    }
    if (kind === 'triangle') {
        return {
            ...base,
            type: 'triangle',
            width: w,
            height: h,
        }
    }
    if (kind === 'star') {
        return {
            ...base,
            type: 'polygon',
            points:
                storedPoints.length >= 3
                    ? storedPoints
                    : boxPoints(x, y, w, h),
            toolId: 'star',
        }
    }
    if (kind === 'roundedrect' || kind === 'callout') {
        return {
            ...base,
            type: 'rect',
            width: w,
            height: h,
            rx: Number(shape.rx) || (kind === 'callout' ? 8 : 12),
            ry: Number(shape.ry) || (kind === 'callout' ? 8 : 12),
        }
    }
    if (kind === 'dashedrect') {
        return {
            ...base,
            type: 'rect',
            width: w,
            height: h,
            strokeDashArray: [10, 6],
        }
    }
    if (
        kind === 'line' ||
        kind === 'arrow' ||
        kind === 'doublearrow' ||
        kind === 'arrowtext' ||
        kind === 'dashedline' ||
        kind === 'dimension' ||
        kind === 'sectionmarker' ||
        kind === 'elevationmarker'
    ) {
        const x1 = Number.isFinite(Number(shape.x1)) ? Number(shape.x1) : x
        const y1 = Number.isFinite(Number(shape.y1)) ? Number(shape.y1) : y
        const x2 = Number.isFinite(Number(shape.x2)) ? Number(shape.x2) : x + w
        const y2 = Number.isFinite(Number(shape.y2)) ? Number(shape.y2) : y + h
        return {
            ...base,
            type: 'line',
            x1,
            y1,
            x2,
            y2,
            originX: 'center',
            originY: 'center',
            fill: null,
            strokeDashArray: kind === 'dashedline' ? [10, 6] : undefined,
        }
    }
    if (kind === 'cross') {
        return {
            ...base,
            type: 'group',
            left: cx,
            top: cy,
            originX: 'center',
            originY: 'center',
            fill: null,
            objects: [
                {
                    type: 'line',
                    x1: -10,
                    y1: -10,
                    x2: 10,
                    y2: 10,
                    stroke: color,
                    strokeWidth: 2,
                },
                {
                    type: 'line',
                    x1: -10,
                    y1: 10,
                    x2: 10,
                    y2: -10,
                    stroke: color,
                    strokeWidth: 2,
                },
            ],
            toolId: 'cross',
        }
    }
    if (STAMP_STYLES[kind]) {
        const stamp = STAMP_STYLES[kind]
        return {
            ...base,
            type: 'group',
            left: cx,
            top: cy,
            originX: 'center',
            originY: 'center',
            fill: null,
            stroke: stamp.fill,
            objects: [
                {
                    type: 'circle',
                    radius: 14,
                    originX: 'center',
                    originY: 'center',
                    fill: null,
                    stroke: stamp.fill,
                    strokeWidth: 2,
                },
                {
                    type: 'text',
                    text: stamp.glyph,
                    originX: 'center',
                    originY: 'center',
                    fill: stamp.fill,
                    fontSize: 14,
                    fontWeight: 'bold',
                },
            ],
            toolId: kind,
        }
    }
    if (kind === 'polygon' && storedPoints.length >= 3) {
        return {
            ...base,
            type: 'polygon',
            points: storedPoints,
        }
    }
    if (kind === 'path' && shape.path) {
        return {
            ...base,
            type: 'path',
            path: shape.path,
            left: cx,
            top: cy,
            originX: shape.originX || 'center',
            originY: shape.originY || 'center',
        }
    }
    return {
        ...base,
        type: 'rect',
        width: w,
        height: h,
        toolId: kind === 'rect' ? 'rect' : kind,
    }
}

const buildSnapshotFromApiComments = (apiComments, designWidth) => {
    const list = Array.isArray(apiComments) ? apiComments : []
    const comments = []
    const objects = []
    list.forEach((item) => {
        const editorComment = toEditorComment(item)
        if (!editorComment) return
        comments.push(editorComment)
        const fabricObj = shapeToFabricObject(
            item.shape,
            editorComment.annotationId,
            item.screen
        )
        if (fabricObj) objects.push(fabricObj)
    })
    const width = designWidth || 1440
    return {
        ...EMPTY_SNAPSHOT,
        canvasWidth: width,
        canvasHeight: Math.round(width * 0.75),
        comments,
        annotations: { version: '5', objects },
    }
}

/** Build Prototype API shape from a selected Fabric object. */
const shapeFromFabricObject = (obj) => {
    if (!obj || obj.isBackground || obj.isCommentMarker) return null
    let left = Number(obj.left) || 0
    let top = Number(obj.top) || 0
    let width = Math.abs(
        Number(
            typeof obj.getScaledWidth === 'function'
                ? obj.getScaledWidth()
                : (obj.width || 0) * (obj.scaleX || 1)
        )
    )
    let height = Math.abs(
        Number(
            typeof obj.getScaledHeight === 'function'
                ? obj.getScaledHeight()
                : (obj.height || 0) * (obj.scaleY || 1)
        )
    )
    if (typeof obj.getBoundingRect === 'function') {
        try {
            const bound = obj.getBoundingRect()
            if (bound) {
                left = Number(bound.left) || left
                top = Number(bound.top) || top
                width = Math.abs(Number(bound.width) || width)
                height = Math.abs(Number(bound.height) || height)
            }
        } catch {
            // keep scaled fallbacks
        }
    }
    const w = Math.max(1, Math.round(width))
    const h = Math.max(1, Math.round(height))
    const toolId = String(
        obj.toolId ||
            (typeof obj.get === 'function' ? obj.get('toolId') : '') ||
            ''
    ).toLowerCase()
    const type = String(
        obj.type || obj.constructor?.type || ''
    ).toLowerCase()
    const kind = normalizeShapeKind({ kind: toolId || type }, type)
    const stroke =
        typeof obj.stroke === 'string' && obj.stroke
            ? obj.stroke
            : kind === 'text'
              ? TEXT_LABEL_FILL
              : COMMENT_SHAPE_COLOR
    const fill =
        typeof obj.fill === 'string' && obj.fill && obj.fill !== 'transparent'
            ? obj.fill
            : stroke
    const points = Array.isArray(obj.points)
        ? obj.points.map((pt) => ({
              x: Number(pt?.x) || 0,
              y: Number(pt?.y) || 0,
          }))
        : undefined
    const path =
        typeof obj.path === 'string'
            ? obj.path
            : Array.isArray(obj.path)
              ? obj.path
              : undefined
    return {
        kind,
        x: Math.round(left),
        y: Math.round(top),
        w,
        h,
        color: kind === 'text' ? fill : stroke,
        originX: obj.originX || undefined,
        originY: obj.originY || undefined,
        ...(Number.isFinite(Number(obj.scaleX))
            ? { scaleX: Number(obj.scaleX) }
            : {}),
        ...(Number.isFinite(Number(obj.scaleY))
            ? { scaleY: Number(obj.scaleY) }
            : {}),
        ...(Number.isFinite(Number(obj.rx)) ? { rx: Number(obj.rx) } : {}),
        ...(Number.isFinite(Number(obj.ry)) ? { ry: Number(obj.ry) } : {}),
        ...(Number.isFinite(Number(obj.x1))
            ? {
                  x1: Number(obj.x1),
                  y1: Number(obj.y1) || 0,
                  x2: Number(obj.x2) || 0,
                  y2: Number(obj.y2) || 0,
              }
            : {}),
        ...(points?.length ? { points } : {}),
        ...(path ? { path } : {}),
        ...(kind === 'text'
            ? {
                  text: String(obj.text || '').trim() || 'Label',
                  fontSize: Math.max(12, Math.round(Number(obj.fontSize) || 20)),
              }
            : {}),
    }
}

/** Same shape fields sent when creating a comment — no leftover API keys. */
const API_SHAPE_KEYS = [
    'kind',
    'x',
    'y',
    'w',
    'h',
    'color',
    'originX',
    'originY',
    'scaleX',
    'scaleY',
    'rx',
    'ry',
    'x1',
    'y1',
    'x2',
    'y2',
    'points',
    'path',
    'text',
    'fontSize',
    'annotationId',
]

const toApiCommentShape = (shape, annotationId) => {
    if (!shape || typeof shape !== 'object') return null
    const next = {}
    API_SHAPE_KEYS.forEach((key) => {
        if (shape[key] != null) next[key] = shape[key]
    })
    if (annotationId) next.annotationId = annotationId
    return Object.keys(next).length ? next : null
}


const getPrototypeHtmlIframe = (host) =>
    host?.querySelector?.('.html-background-layer iframe') ?? null

const htmlElementPath = (node) => {
    if (!node || !node.ownerDocument) return ''
    const parts = []
    let current = node
    const root = node.ownerDocument.documentElement
    while (current && current !== root) {
        const parent = current.parentElement
        if (!parent) break
        const index = Array.prototype.indexOf.call(parent.children, current)
        parts.unshift(
            `${current.tagName.toLowerCase()}:nth-child(${index + 1})`
        )
        current = parent
    }
    return parts.length ? `html > ${parts.join(' > ')}` : 'html'
}

const ACTIONABLE_SELECTOR =
    "button, a[href], [role='button'], [role='link'], [role='tab'], [role='menuitem'], [role='option'], [onclick], input, select, textarea, label, summary, [tabindex]"

/** Controls navigated by CHOOSING: a select, a radio, a checkbox. */
const isValueControl = (el) => {
    if (!el || typeof el.tagName !== 'string') return false
    const tag = el.tagName.toLowerCase()
    if (tag === 'select') return true
    if (tag !== 'input') return false
    const type = String(el.type || '').toLowerCase()
    return type === 'radio' || type === 'checkbox'
}

/**
 * Any control whose value belongs in the route — choices plus typed text.
 *
 * Text was left out on the reasoning that what someone types is content rather
 * than navigation. The prototypes disagree: a wizard that will not enable
 * Continue until the form is filled, and — because the screen fingerprint is a
 * hash of the page's visible text — any screen that shows back what was
 * entered. Replaying the same values is what makes both reachable again.
 *
 * `isTextValueControl` comes from the package so capture and replay cannot
 * drift apart, and it screens out passwords, card numbers and the like: those
 * are never recorded, whatever it costs the route.
 */
const isRecordableValueControl = (el) =>
    isValueControl(el) || isTextValueControl(el)

/**
 * Match markup-editor actionableAncestor — never fall back to inert content.
 *
 * Returns the OUTERMOST pointer-cursor ancestor, not the first one found.
 * `cursor` is inherited, so every child of a clickable card reports `pointer`
 * too — stopping at the first match records the <span> holding the label
 * instead of the card that is actually wired up, and a route recorded against
 * that span replays into nothing. That is the silent "redirect does nothing"
 * failure: the trigger resolves to a real element, the click lands, and the
 * prototype never moves.
 *
 * Scans the full depth rather than breaking at the first non-pointer ancestor:
 * a child inside a clickable card may set `cursor: default` of its own.
 */
const actionableAncestor = (el) => {
    if (!el || typeof el.closest !== 'function') return null
    const marked = el.closest(ACTIONABLE_SELECTOR)
    if (marked) return marked
    const win = el.ownerDocument?.defaultView
    if (!win) return null
    let current = el
    let outermost = null
    for (let i = 0; i < 6 && current; i += 1) {
        try {
            if (win.getComputedStyle(current).cursor === 'pointer') {
                outermost = current
            }
        } catch {
            break
        }
        current = current.parentElement
    }
    return outermost
}

const triggerFromEventTarget = (target) => {
    const el = actionableAncestor(target)
    if (!el || typeof el.tagName !== 'string') return null
    const base = {
        path: htmlElementPath(el),
        text: String(el.textContent || '')
            .replace(/\s+/g, ' ')
            .trim()
            .slice(0, 60),
        tag: el.tagName,
    }
    if (!isRecordableValueControl(el)) return base
    if (el.tagName.toLowerCase() === 'select') {
        return { ...base, value: String(el.value || '') }
    }
    if (isTextValueControl(el)) {
        // Typed text. `isTextValueControl` has already refused anything that
        // looks like a secret, so what reaches here is form content the route
        // genuinely depends on.
        return { ...base, value: String(el.value || '') }
    }
    return {
        ...base,
        value: String(el.value || ''),
        checked: Boolean(el.checked),
    }
}

/**
 * Is the editor currently walking the prototype to a comment's screen?
 *
 * While it is, everything happening in the iframe is the EDITOR clicking, not
 * the reviewer navigating. Recording it writes the replay back into the very
 * route being replayed, so the next redirect follows a path assembled out of
 * the last one — which is why redirecting several times in a row degrades
 * instead of just repeating.
 *
 * The package sets this attribute on its root for exactly this purpose.
 */
const isEditorRedirecting = (host) =>
    Boolean(host?.querySelector?.('[data-markup-redirecting="true"]')) ||
    host?.getAttribute?.('data-markup-redirecting') === 'true'

const waitMs = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms))


const getFabricCanvasFromHost = (host, fallbackObject) => {
    if (fallbackObject?.canvas) return fallbackObject.canvas
    if (!host) return null
    const wrap = host.querySelector?.('.canvas-container')
    const nodes = [
        wrap,
        wrap?.querySelector?.('canvas.lower-canvas'),
        wrap?.querySelector?.('canvas.upper-canvas'),
        host.querySelector?.('canvas.lower-canvas'),
    ].filter(Boolean)
    for (const node of nodes) {
        for (const key of Reflect.ownKeys(node)) {
            const value = node[key]
            if (
                value &&
                typeof value.getObjects === 'function' &&
                typeof value.requestRenderAll === 'function'
            ) {
                return value
            }
        }
    }
    return null
}


const applyCommentHtmlScreensToCanvas = (host, comments, fallbackObject) => {
    const canvas = getFabricCanvasFromHost(host, fallbackObject)
    if (!canvas || typeof canvas.getObjects !== 'function') return false
    const screenByAnnotation = new Map()
    ;(Array.isArray(comments) ? comments : []).forEach((item) => {
        const annotationId = String(item?.annotationId || item?.id || '')
        const hash = String(item?.screen?.hash || '')
        if (annotationId && hash) screenByAnnotation.set(annotationId, hash)
    })
    if (!screenByAnnotation.size) return false

    const objects = canvas.getObjects()
    if (!objects.length) return false

    objects.forEach((obj) => {
        if (obj?.isBackground || obj?.isCommentMarker) return
        const annotationId = String(obj.annotationId || obj.id || '')
        if (!annotationId) return
        if (!obj.annotationId && obj.id) {
            obj.annotationId = obj.id
            obj.set?.('annotationId', obj.id)
        }
        const hash = screenByAnnotation.get(annotationId)
        if (!hash) return
        obj.htmlScreenId = hash
        obj.set?.('htmlScreenId', hash)
    })

    let current = ''
    try {
        current = screenIdForIframe(getPrototypeHtmlIframe(host))
    } catch {
        current = ''
    }
    if (
        !current ||
        current === 'html:loading' ||
        current === 'html:none' ||
        current === 'html:initial'
    ) {
        canvas.requestRenderAll?.()
        return true
    }

    objects.forEach((obj) => {
        if (obj?.isBackground) return
        let screenId = obj.htmlScreenId
        if (obj.isCommentMarker && obj.linkedAnnotationId) {
            const linked = objects.find(
                (item) =>
                    String(item.annotationId || '') ===
                    String(obj.linkedAnnotationId)
            )
            screenId = linked?.htmlScreenId || screenId
        }
        const visible = screenId === current
        obj.set?.({
            visible,
            evented: visible,
            selectable: visible && !obj.isCommentMarker,
            dirty: true,
            objectCaching: false,
        })
    })
    canvas.requestRenderAll?.()
    return true
}


const exitHtmlBrowseMode = (host) => {
    const btn = host?.querySelector?.(
        '[aria-label="Browse live page"][aria-pressed="true"]'
    )
    if (btn instanceof HTMLElement) btn.click()
}


const screenFromFabricObject = (obj) => {
    const hash = obj?.htmlScreenId
    if (!hash) return undefined
    return {
        hash,
        visibleIds: [hash],
        label: hash,
    }
}

const htmlAnchorFromObject = (obj) =>
    obj?.htmlAnchor ||
    (typeof obj?.get === 'function' ? obj.get('htmlAnchor') : null)

const readIframeScrollY = (iframe) => {
    try {
        const win = iframe?.contentWindow
        const doc = iframe?.contentDocument
        const y = Number(
            win?.scrollY ??
                win?.pageYOffset ??
                doc?.scrollingElement?.scrollTop ??
                doc?.documentElement?.scrollTop ??
                0
        )
        return Number.isFinite(y) ? Math.round(y) : 0
    } catch {
        return 0
    }
}

/** Structural signature of the live prototype, straight from the package. */
const readLiveHtmlSignature = (host) => {
    try {
        return getHtmlScreenSignature(getPrototypeHtmlIframe(host)) || ''
    } catch {
        return ''
    }
}

const readLiveHtmlScreenId = (host) => {
    const iframe = getPrototypeHtmlIframe(host)
    let liveScreenId = ''
    try {
        liveScreenId = screenIdForIframe(iframe)
    } catch {
        liveScreenId = ''
    }
    if (
        liveScreenId === 'html:loading' ||
        liveScreenId === 'html:none' ||
        liveScreenId === 'html:initial'
    ) {
        liveScreenId = ''
    }
    return { iframe, liveScreenId }
}

/** Live HTML screen payload for comment create/update (hash, trigger, chain, from). */
const buildLiveApiCommentScreen = ({
    selected,
    host,
    htmlScreenId,
    lastTrigger,
    clickChain,
    fromScreenId,
    previousScreen,
    snapshot,
}) => {
    const { iframe, liveScreenId: iframeScreenId } = readLiveHtmlScreenId(host)
    const liveScreenId =
        String(htmlScreenId || '').trim() ||
        iframeScreenId ||
        String(previousScreen?.hash || '')
    const htmlAnchor = htmlAnchorFromObject(selected)
    const fabricScreen = screenFromFabricObject(selected)
    const from =
        fromScreenId && fromScreenId !== liveScreenId
            ? fromScreenId
            : previousScreen?.from || null
    // `htmlAnchor` is deliberately absent from both — see toApiCommentScreen.
    const trigger = lastTrigger || previousScreen?.trigger || null
    const chain =
        Array.isArray(clickChain) && clickChain.length > 0
            ? clickChain.slice()
            : Array.isArray(previousScreen?.chain)
              ? previousScreen.chain
              : []
    return toApiCommentScreen({
        ...(fabricScreen || {}),
        ...(liveScreenId
            ? {
                  hash: liveScreenId,
                  visibleIds: [liveScreenId],
                  label: liveScreenId,
              }
            : {}),
        scrollY: readIframeScrollY(iframe),
        signature: readLiveHtmlSignature(host) || previousScreen?.signature,
        trigger,
        chain,
        from,
        htmlAnchor,
        // Keep whatever the comment already carried when this edit did not
        // recapture — a body edit must not cost the comment its snapshot.
        snapshot: snapshot || previousScreen?.snapshot,
    })
}

/**
 * Did the API keep the fields we sent, or quietly drop them?
 *
 * The comments API has a whitelist on `screen`: it accepts what it declares and
 * silently discards the rest. That is invisible from here — the POST succeeds,
 * the comment appears, and only redirect is worse for it, weeks later and with
 * no clue why.
 *
 * Measured against four real comments: `recorder` is sent unconditionally and
 * came back absent, as did `signature`, `landsOn` on every chain step, and
 * `value` on a `<select>` step. Each loss costs something specific:
 *
 *   - landsOn   replay can no longer tell whether a step actually navigated, so
 *               it fires every click blind and cannot retry the one that missed
 *   - value     a <select> step replays as a click, which prototypes ignore
 *   - snapshot  the screen cannot be restored at all for anyone who was not
 *               there, which is every teammate
 *
 * Warns once per session per field, in development only. The point is to make
 * the schema gap loud the first time somebody comments after a deploy, and to
 * go quiet by itself the moment the backend accepts them.
 */
const warnedDroppedFields = new Set()
const reportDroppedScreenFields = (sent, returned) => {
    if (process.env.NODE_ENV === 'production') return
    if (!sent || typeof sent !== 'object') return
    const back = returned && typeof returned === 'object' ? returned : {}

    const missing = []
    for (const key of ['snapshot', 'signature', 'recorder']) {
        if (sent[key] != null && back[key] == null) missing.push(`screen.${key}`)
    }
    const sentSteps = Array.isArray(sent.chain) ? sent.chain : []
    const backSteps = Array.isArray(back.chain) ? back.chain : []
    for (const key of ['landsOn', 'value', 'checked']) {
        const sentAny = sentSteps.some((step) => step && step[key] !== undefined)
        const backAny = backSteps.some((step) => step && step[key] !== undefined)
        if (sentAny && !backAny) missing.push(`screen.chain[].${key}`)
    }
    if (sentSteps.length > backSteps.length) {
        missing.push(
            `screen.chain truncated ${sentSteps.length} -> ${backSteps.length}`
        )
    }

    const fresh = missing.filter((name) => !warnedDroppedFields.has(name))
    if (!fresh.length) return
    fresh.forEach((name) => warnedDroppedFields.add(name))
    // eslint-disable-next-line no-console
    console.warn(
        '[pmx] the comments API dropped fields the editor needs for redirect: ' +
            fresh.join(', ') +
            '. Comment redirect will fall back to blind click replay. ' +
            'These have to be accepted on the screen sub-schema server-side.'
    )
}

const annotationIdList = (ids) =>
    (Array.isArray(ids) ? ids : [])
        .map((id) => String(id || ''))
        .filter(Boolean)

const commentsLinkedToAnnotations = (comments, annotationIds) => {
    const idSet = new Set(annotationIdList(annotationIds))
    if (!idSet.size) return []
    return (Array.isArray(comments) ? comments : []).filter((item) =>
        idSet.has(String(item.annotationId || ''))
    )
}

/** Normalize onAnnotationMoved box (`left/top/width/height` or `x/y/w/h`). */
const annotationBoxCoords = (box) => {
    if (!box || typeof box !== 'object') return null
    const x = Number(box.left ?? box.x)
    const y = Number(box.top ?? box.y)
    const w = Number(box.width ?? box.w)
    const h = Number(box.height ?? box.h)
    if (!Number.isFinite(x) || !Number.isFinite(y)) return null
    return {
        x: Math.round(x),
        y: Math.round(y),
        ...(Number.isFinite(w) ? { w: Math.max(1, Math.round(w)) } : {}),
        ...(Number.isFinite(h) ? { h: Math.max(1, Math.round(h)) } : {}),
    }
}

/** Apply a drag/resize box onto a stored comment shape (including lines/points). */
const applyMovedBoxToShape = (shape, fromBox, toBox, annotationId, toolId) => {
    const next = { ...(shape || {}), annotationId }
    if (toolId) {
        next.kind = normalizeShapeKind({ kind: toolId || next.kind }, toolId)
    }
    const from = annotationBoxCoords(fromBox)
    const to = annotationBoxCoords(toBox)
    if (!to) return next
    const dx = from ? to.x - from.x : 0
    const dy = from ? to.y - from.y : 0
    next.x = to.x
    next.y = to.y
    if (to.w != null) next.w = to.w
    if (to.h != null) next.h = to.h
    if (dx || dy) {
        if (Number.isFinite(Number(next.x1))) {
            next.x1 = Number(next.x1) + dx
            next.y1 = (Number(next.y1) || 0) + dy
            next.x2 = (Number(next.x2) || 0) + dx
            next.y2 = (Number(next.y2) || 0) + dy
        }
        if (Array.isArray(next.points)) {
            next.points = next.points.map((pt) => ({
                x: (Number(pt?.x) || 0) + dx,
                y: (Number(pt?.y) || 0) + dy,
            }))
        }
    }
    return next
}

const formatDateTime = (value) => {
    if (!value) return ''
    const parsed = dayjs(value)
    return parsed.isValid() ? parsed.format('D MMM YYYY, h:mm A') : ''
}

const ACTIVITY_KIND_LABELS = {
    prototype_created: 'Created',
    version_uploaded: 'Uploaded',
    version_activated: 'Activated',
    comment_added: 'Comment',
    comment_deleted: 'Comment',
    comment_resolved: 'Resolved',
    comment_reopened: 'Reopened',
    comment_edited: 'Comment',
    reply_added: 'Reply',
}

const ACTIVITY_PACKAGE_ACTION = {
    comment_added: 'comment_added',
    comment_deleted: 'comment_deleted',
    comment_edited: 'comment_edited',
    reply_added: 'comment_replied',
}

const clipCommentBody = (value, max = 140) => {
    const text = String(value || '')
        .replace(/\s+/g, ' ')
        .trim()
    if (!text) return ''
    return text.length > max ? `${text.slice(0, max - 1)}…` : text
}

const isMovedCommentActivity = (kind, apiText) =>
    kind === 'comment_edited' && /moved a comment/i.test(apiText || '')

const formatActivitySummary = (kind, apiText, data) => {
    const body = clipCommentBody(data.body)
    const previousBody = clipCommentBody(data.previousBody)
    const withBody = (label) => (body ? `${label}: ${body}` : label)
    if (kind === 'comment_added') return withBody('Added a comment')
    if (kind === 'comment_deleted') return withBody('Deleted a comment')
    if (kind === 'comment_resolved') return withBody('Resolved a comment')
    if (kind === 'comment_reopened') return withBody('Reopened a comment')
    if (kind === 'comment_edited') {
        if (isMovedCommentActivity(kind, apiText)) {
            return withBody('Moved a comment')
        }
        if (previousBody && body) {
            return `Edited a comment: ${previousBody} → ${body}`
        }
        return withBody('Edited a comment')
    }
    if (kind === 'reply_added') return withBody('Replied to a comment')
    if (kind === 'version_uploaded') {
        const version =
            data.version != null ? `version v${data.version}` : 'a version'
        const note = data.changeNote ? ` — ${data.changeNote}` : ''
        const carried =
            data.carried != null
                ? ` (${data.carried} comment${
                      data.carried === 1 ? '' : 's'
                  } carried)`
                : ''
        return apiText && !/^uploaded version\b/i.test(apiText)
            ? apiText
            : `Uploaded ${version}${note}${carried}`
    }
    if (kind === 'version_activated') {
        if (data.version != null) return `Switched to version v${data.version}`
        return apiText || 'Switched version'
    }
    if (kind === 'prototype_created') {
        return data.name ? `Created “${data.name}”` : apiText || 'Prototype created'
    }
    return apiText || ACTIVITY_KIND_LABELS[kind] || 'Activity'
}

/** Build a readable history row from Prototype activity API items. */
const toHistoryActivityItem = (item, index, prototypeId) => {
    const kind = String(item?.kind || item?.action || item?.type || '').trim()
    const data = item?.data && typeof item.data === 'object' ? item.data : {}
    const apiText = String(
        item?.text || item?.summary || item?.message || ''
    ).trim()
    const summary = formatActivitySummary(kind, apiText, data)
    const action = ACTIVITY_PACKAGE_ACTION[kind] || ACTIVITY_KIND_LABELS[kind] || kind || 'update'

    return {
        id: String(item?._id || `activity-${index}`),
        drawingId: prototypeId,
        versionId: item?.versionId ? String(item.versionId) : null,
        kind,
        title: ACTIVITY_KIND_LABELS[kind] || summary,
        summary,
        detail: summary,
        text: apiText || null,
        data,
        userId: String(item?.actorId || item?.userId || item?.createdBy || ''),
        userName:
            item?.actorName ||
            item?.userName ||
            item?.createdByName ||
            'User',
        actorType: item?.actorType || null,
        action,
        createdAt: item?.createdAt || new Date().toISOString(),
    }
}

const isHtmlFile = (file) => {
    if (!file?.name) return false
    const lower = file.name.toLowerCase()
    return lower.endsWith('.html') || lower.endsWith('.htm')
}

const persistDrawingIdForPrototype = (prototypeId, versionId, drawingId) =>
    persistCachedDrawingId(prototypeId, versionId, drawingId)

const clearDrawingIdForPrototype = (prototypeId, versionId) =>
    clearCachedDrawingIdEverywhere(prototypeId, versionId)

const fetchHtmlSource = async (fileUrl) => {
    const response = await fetch(fileUrl, { credentials: 'omit' })
    if (!response.ok) {
        throw new Error(`Failed to load prototype HTML (${response.status})`)
    }
    return response.text()
}

class EditorErrorBoundary extends React.Component {
    constructor(props) {
        super(props)
        this.state = { error: null }
    }

    static getDerivedStateFromError(error) {
        return { error }
    }

    componentDidCatch() {
        this.props.onError?.()
    }

    render() {
        if (this.state.error) {
            return (
                <div className={styles.errorState}>
                    <div>
                        Drawing editor hit an error
                        {this.state.error?.message
                            ? `: ${this.state.error.message}`
                            : ''}
                    </div>
                    <button
                        type="button"
                        className={styles.ghostButton}
                        onClick={() => {
                            this.setState({ error: null })
                            this.props.onRetry?.()
                        }}
                    >
                        Retry
                    </button>
                </div>
            )
        }
        return this.props.children
    }
}

const ProjectPrototypeDetailsView = ({
    prototypeId,
    editorUser,
    onBack,
    readOnly = false,
    canDelete = false,
    isSuperAdmin = false,
    isClientUser = false,
}) => {
    const [detail, setDetail] = useState(null)
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const [drawingId, setDrawingId] = useState(null)
    const [editorMountKey, setEditorMountKey] = useState(0)
    const [fullWidth, setFullWidth] = useState(false)
    const [comments, setComments] = useState([])
    const [selectedAnnotationId, setSelectedAnnotationId] = useState(null)
    const [selectedObject, setSelectedObject] = useState(null)
    // const [uploadingVersion, setUploadingVersion] = useState(false)
    const [versionId, setVersionId] = useState(null)
    const [currentVersionId, setCurrentVersionId] = useState(null)
    const [switchingVersionId, setSwitchingVersionId] = useState(null)
    const [rightTab, setRightTab] = useState(HOST_COMMENTS_TAB)
    const [restoringVersionId, setRestoringVersionId] = useState(null)
    const [historyActivity, setHistoryActivity] = useState([])
    const versionInputRef = useRef(null)
    const editorRef = useRef(null)
    const openSeqRef = useRef(0)
    const hostRef = useRef(null)
    const selectedObjectRef = useRef(null)
    const syncingCommentRef = useRef(false)
    const selectedAnnotationIdRef = useRef(null)
    const commentsRef = useRef([])
    const pinnedCommentsRef = useRef(new Set())
    const lastHtmlTriggerRef = useRef(null)
    const lastHtmlScreenIdRef = useRef('')
    const fromHtmlScreenIdRef = useRef('')
    /**
     * The click path from the load screen to wherever the prototype is now,
     * as `[{ trigger, screenId }]`.
     *
     * Screen-attributed rather than a raw click log, because the two are very
     * different things. A log contains every click the reviewer made — opening a
     * dropdown, closing it again, tapping inert chrome that happened to carry a
     * pointer cursor — and replaying it fires clicks the prototype never treated
     * as navigation. Only clicks that actually moved the fingerprint get in
     * here, so what ships to the API is the shortest sequence that reproduces
     * the screen.
     *
     * Written by the screen-watching tick alone. Both the tick and the click
     * handler used to write it, and they raced.
     */
    const htmlClickPathRef = useRef([])
    /** The last click, waiting to be credited with a screen change (or not). */
    const pendingClickRef = useRef(null)
    /**
     * The recorded path as the package's `chain`.
     *
     * Steps the prototype took by itself carry no trigger and drop out — there
     * is nothing to replay for them — but each surviving trigger keeps the
     * screen it landed on, which is what lets replay verify itself step by step
     * rather than firing the whole sequence and hoping.
     */
    const recordedClickChain = useCallback(
        () =>
            htmlClickPathRef.current
                .filter((step) => step.trigger)
                .map((step) => ({ ...step.trigger, landsOn: step.screenId })),
        []
    )
    /**
     * Comment creates currently in flight, keyed by annotation.
     *
     * Without this a second submit lands before the first POST returns and the
     * API stores the comment twice — identical body, identical shape
     * coordinates, ids milliseconds apart. Guarding on the annotation rather
     * than a single boolean so two comments on two different shapes can still
     * be created at once.
     */
    const creatingCommentIdsRef = useRef(new Set())
    const hostDeletedAnnotationIdsRef = useRef(new Set())
    const shapeDeleteConfirmRef = useRef(null)
    const [hostReady, setHostReady] = useState(false)
    const [markersLive, setMarkersLive] = useState(false)
    const [editorSettled, setEditorSettled] = useState(false)
    // const [historyVersions, setHistoryVersions] = useState([])
    const [replyDrafts, setReplyDrafts] = useState({})
    const [replyOpenId, setReplyOpenId] = useState(null)
    const [editingId, setEditingId] = useState(null)
    const [editDraft, setEditDraft] = useState('')
    const [commentDraft, setCommentDraft] = useState('')
    const [busyCommentId, setBusyCommentId] = useState(null)
    const [tabBarSlot, setTabBarSlot] = useState(null)
    const [deleteTarget, setDeleteTarget] = useState(null)
    const [deleteLoading, setDeleteLoading] = useState(false)
    const [exportingComments, setExportingComments] = useState(false)

    // Package HTML zoom is on the view-island buttons. Ctrl/Cmd+wheel clicks those.
    useEffect(() => {
        const node = hostRef.current
        if (!node || !editorSettled) return undefined
        const onWheel = (event) => {
            if (!(event.ctrlKey || event.metaKey)) return
            event.preventDefault()
            const label =
                event.deltaY > 0 ? 'Zoom out' : 'Zoom in'
            node.querySelector(`[aria-label="${label}"]`)?.click()
        }
        node.addEventListener('wheel', onWheel, { passive: false })
        return () => node.removeEventListener('wheel', onWheel)
    }, [hostReady, editorSettled, drawingId, editorMountKey])

    commentsRef.current = comments

    // After layout settles, nudge Fabric/HTML layer to fill the host.
    useEffect(() => {
        if (!editorSettled) return undefined
        const timer = window.setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
        return () => window.clearTimeout(timer)
    }, [editorSettled, drawingId, editorMountKey, fullWidth])

    const openInEditor = useCallback(
        async (prototypeDetail, user, apiComments = []) => {
            const version =
                prototypeDetail.version || prototypeDetail.currentVersion
            const nextVersionId =
                version?._id || prototypeDetail.currentVersionId || 'current'
            const fileUrl = version?.fileUrl
            if (!fileUrl) {
                throw new Error('This prototype has no HTML file URL')
            }
            if (!user) {
                throw new Error('You must be signed in to open the editor')
            }

            setVersionId(nextVersionId)
            setCurrentVersionId(String(nextVersionId))

            const mappedComments = sortCommentsNewestFirst(
                (apiComments || []).map(toEditorComment).filter(Boolean)
            )
            setComments(mappedComments)

            const snapshot = toPackageSnapshot(
                buildSnapshotFromApiComments(
                    apiComments,
                    prototypeDetail.designWidth
                ),
                mappedComments
            )

            const cachedId = await resolveCachedDrawingId(
                prototypeId,
                nextVersionId
            )
            if (cachedId) {
                try {
                    const existing = await loadDrawingState(cachedId)
                    if (existing?.drawing?.id) {
                        try {
                            await saveVersion(existing.drawing.id, snapshot, user, {
                                label: 'Sync prototype comments',
                                source: 'host',
                            })
                        } catch {
                            // Keep the cached drawing even if snapshot sync fails.
                        }
                        await restoreHtmlScreenRoutes(
                            existing.drawing.id,
                            prototypeId,
                            nextVersionId,
                            mappedComments,
                            prototypeDetail.designWidth
                        )
                        await persistDrawingIdForPrototype(
                            prototypeId,
                            nextVersionId,
                            existing.drawing.id
                        )
                        setDrawingId(existing.drawing.id)
                        setComments(mappedComments)
                        setRightTab(HOST_COMMENTS_TAB)
                        return nextVersionId
                    }
                } catch {
                    // Cached drawing is gone — create a fresh one below.
                }
                await clearDrawingIdForPrototype(prototypeId, nextVersionId)
            }

            const html = await fetchHtmlSource(fileUrl)
            const created = await createDrawing(
                user,
                prototypeDetail.name || 'Prototype',
                html,
                snapshot,
                null,
                'html'
            )

            const nextId = created?.drawing?.id
            if (!nextId) {
                throw new Error('Drawing editor failed to create a drawing')
            }

            await persistDrawingIdForPrototype(
                prototypeId,
                nextVersionId,
                nextId
            )
            await restoreHtmlScreenRoutes(
                nextId,
                prototypeId,
                nextVersionId,
                mappedComments,
                prototypeDetail.designWidth
            )
            setDrawingId(nextId)
            setComments(mappedComments)
            setRightTab(HOST_COMMENTS_TAB)
            return nextVersionId
        },
        [prototypeId]
    )

    const load = useCallback(
        async () => {
            const seq = ++openSeqRef.current
            setLoading(true)
            setLoadError('')
            setDrawingId(null)
            setComments([])
            // setHistoryVersions([])
            setHistoryActivity([])
            setCurrentVersionId(null)
            setHostReady(false)
            setEditorSettled(false)

            const [detailRes, versionsRes] = await Promise.all([
                getPrototypeDetail(prototypeId),
                listPrototypeVersions(prototypeId),
            ])
            if (seq !== openSeqRef.current) return

            if (!detailRes.success || !detailRes.result) {
                setLoading(false)
                setLoadError(detailRes.message || 'Failed to load prototype')
                return
            }

            setDetail(detailRes.result)
            const activeId = String(
                versionsRes.currentVersionId ||
                    detailRes.result?.currentVersionId ||
                    detailRes.result?.currentVersion?._id ||
                    ''
            )
            if (activeId) setCurrentVersionId(activeId)

            const [commentsRes, activityRes] = await Promise.all([
                listPrototypeComments(prototypeId, {
                    ...(activeId ? { versionId: activeId, isOrphaned: false } : {}),
                }),
                listPrototypeActivity(prototypeId, {
                    ...(activeId ? { versionId: activeId } : {}),
                    skip: 0,
                    limit: 100,
                }),
            ])
            if (seq !== openSeqRef.current) return

            const apiComments = commentsRes.success
                ? Array.isArray(commentsRes.result)
                    ? commentsRes.result
                    : Array.isArray(commentsRes.data)
                      ? commentsRes.data
                      : []
                : []
            if (!commentsRes.success) {
                message.warning(
                    commentsRes.message ||
                        'Could not load prototype comments from the server'
                )
            }

            /* if (versionsRes.success) {
                setHistoryVersions(
                    (versionsRes.result || []).map((item, index) => {
                        const id = String(item._id || `version-${index}`)
                        return {
                            id,
                            drawingId: prototypeId,
                            versionNumber:
                                item.version ??
                                item.versionNumber ??
                                index + 1,
                            createdAt:
                                item.createdAt || new Date().toISOString(),
                            fileName: item.fileName || '',
                            fileUrl: item.fileUrl || '',
                            fileSize: item.fileSize ?? null,
                            changeNote: item.changeNote || item.label || '',
                            createdBy: {
                                userId: String(
                                    item.createdBy || item.createdById || ''
                                ),
                                userName:
                                    item.uploadedByName ||
                                    item.createdByName ||
                                    item.authorName ||
                                    'User',
                            },
                            label:
                                item.changeNote ||
                                item.label ||
                                item.fileName ||
                                `Version ${item.version ?? index + 1}`,
                            isCurrent: Boolean(activeId) && id === activeId,
                            source: 'prototype',
                            snapshot: EMPTY_SNAPSHOT,
                        }
                    })
                )
            } */

            if (activityRes.success) {
                setHistoryActivity(
                    (activityRes.result || []).map((item, index) =>
                        toHistoryActivityItem(item, index, prototypeId)
                    )
                )
            }

            try {
                await openInEditor(detailRes.result, editorUser, apiComments)
                if (seq !== openSeqRef.current) return
                setLoading(false)
            } catch (error) {
                if (seq !== openSeqRef.current) return
                setLoading(false)
                setLoadError(
                    error?.message ||
                        'Could not open prototype in the drawing editor'
                )
            }
        },
        [prototypeId, editorUser, openInEditor]
    )

    // Reload the editor only when the prototype id changes.
    const loadRef = useRef(load)
    loadRef.current = load
    useEffect(() => {
        loadRef.current()
    }, [prototypeId])

    useEffect(() => {
        if (loading || !drawingId) {
            setHostReady(false)
            setEditorSettled(false)
            return undefined
        }
        let attempts = 0
        let rafId = 0
        const check = () => {
            const node = hostRef.current
            const ready =
                Boolean(node) &&
                node.clientWidth > 0 &&
                node.clientHeight > 0
            if (ready || attempts > 60) {
                setEditorSettled(false)
                setHostReady(true)
                return
            }
            attempts += 1
            rafId = window.requestAnimationFrame(check)
        }
        rafId = window.requestAnimationFrame(check)
        return () => window.cancelAnimationFrame(rafId)
    }, [loading, drawingId])

    const remountEditor = useCallback(() => {
        setEditorMountKey((k) => k + 1)
        setHostReady(false)
        setEditorSettled(false)
        window.requestAnimationFrame(() => setHostReady(true))
    }, [])

    const handleCommentsChange = useCallback(() => {
        // Host commentsPanelProps is the source of truth (API ids).
        // Ignore editor-local IndexedDB comment churn.
    }, [])

    const handleSelectionChange = useCallback((payload) => {
        const nextObject = payload?.selectedObject ?? null
        selectedObjectRef.current = nextObject
        setSelectedObject(nextObject)
        const nextId =
            payload?.selectedAnnotationId ??
            payload?.annotationId ??
            nextObject?.annotationId ??
            null
        selectedAnnotationIdRef.current = nextId ? String(nextId) : null
        setSelectedAnnotationId(nextId ? String(nextId) : null)
        if (nextObject || nextId) {
            setRightTab(HOST_COMMENTS_TAB)
        }
    }, [])

    const handleToggleFullWidth = useCallback(() => {
        setFullWidth((prev) => !prev)
    }, [])

    const refreshCanvasHistory = useCallback(async () => {
        if (!drawingId) return
        try {
            await Promise.all([
                listVersionsByDrawing(drawingId),
                listActivityByDrawing(drawingId),
            ])
        } catch {
            /* History tab reads prototype activity, not canvas IndexedDB. */
        }
    }, [drawingId])

    const handleDirtyChange = useCallback(
        (dirty) => {
            if (!dirty) refreshCanvasHistory()
        },
        [refreshCanvasHistory]
    )

    const handleDrawingCreated = useCallback(() => {
        refreshCanvasHistory()
    }, [refreshCanvasHistory])

    const markEditorSettled = useCallback(() => {
        setEditorSettled(true)
        refreshCanvasHistory()
    }, [refreshCanvasHistory])

    // Fallback if editor callbacks never fire.
    useEffect(() => {
        if (!hostReady || editorSettled) return undefined
        const timer = window.setTimeout(() => setEditorSettled(true), 2000)
        return () => window.clearTimeout(timer)
    }, [hostReady, editorSettled, drawingId, editorMountKey])

    useEffect(() => {
        if (!editorSettled || !drawingId) return undefined
        refreshCanvasHistory()
        return undefined
    }, [editorSettled, drawingId, editorMountKey, refreshCanvasHistory])

    // Wait until the editor has restored annotations before pinning comments.
    useEffect(() => {
        if (!editorSettled || !drawingId) {
            setMarkersLive(false)
            pinnedCommentsRef.current = new Set()
            return undefined
        }
        const timer = window.setTimeout(() => setMarkersLive(true), 350)
        return () => window.clearTimeout(timer)
    }, [editorSettled, drawingId, editorMountKey])

    // Fabric 7 drops htmlScreenId when reviving snapshot JSON, so the editor
    // stamps every shape onto the first HTML page. Re-apply API screen hashes.
    useEffect(() => {
        if (!editorSettled || !drawingId || !comments.length) return undefined
        let tries = 0
        let stamped = false
        let lastScreen = ''
        let timer = 0
        const apply = (force) => {
            let current = ''
            try {
                current = screenIdForIframe(
                    getPrototypeHtmlIframe(hostRef.current)
                )
            } catch {
                current = ''
            }
            if (!force && stamped && current === lastScreen) return
            const ok = applyCommentHtmlScreensToCanvas(
                hostRef.current,
                comments,
                selectedObjectRef.current
            )
            tries += 1
            if (ok) {
                stamped = true
                lastScreen = current
                return
            }
            if (tries < 16) {
                timer = window.setTimeout(() => apply(true), 200)
            }
        }
        timer = window.setTimeout(() => apply(true), 250)
        return () => {
            window.clearTimeout(timer)
        }
    }, [editorSettled, drawingId, editorMountKey, comments])

    useEffect(() => {
        htmlClickPathRef.current = []
        pendingClickRef.current = null
        lastHtmlTriggerRef.current = null
        lastHtmlScreenIdRef.current = ''
        fromHtmlScreenIdRef.current = ''
    }, [drawingId, editorMountKey])

    useEffect(() => {
        if (!editorSettled || !drawingId) return undefined
        const isRealScreen = (id) =>
            Boolean(id) &&
            id !== 'html:loading' &&
            id !== 'html:none' &&
            id !== 'html:initial'

        /**
         * Extend the recorded path by one interaction.
         *
         * `trigger` is null for a screen the prototype reached on its own — a
         * splash resolving, a timer, a redirect — and for the load screen. Those
         * still take a slot: the slot is what lets the detour check below
         * recognise the screen if the reviewer comes back to it.
         */
        const recordStep = (trigger, screenId) => {
            const path = htmlClickPathRef.current
            const first = path.findIndex((step) => step.screenId === screenId)
            // Back on a screen the reviewer genuinely LEFT. The way to it is the
            // prefix, so the detour in between is dropped — without this,
            // A -> B -> A -> C ships four steps for a journey that is really two.
            //
            // Guarded on having actually been somewhere else since. A run of
            // clicks that all sit on one screen is not a detour: it is a menu
            // opening, an accordion expanding, a checkbox that gates the next
            // button. Treating those as a loop and collapsing them is how replay
            // ends up clicking a dropdown item while the dropdown is shut.
            const leftAndReturned =
                first >= 0 && path.slice(first).some((step) => step.screenId !== screenId)
            if (leftAndReturned) {
                htmlClickPathRef.current = path.slice(0, first + 1)
                return
            }
            if (path.length >= MAX_HOST_CHAIN_STEPS) return
            htmlClickPathRef.current = [...path, { trigger, screenId }]
        }

        /**
         * One sample of where the prototype is, and what to credit for it.
         *
         * The single writer of the recorded path. A click on its own proves
         * nothing — the interesting question is what the document looked like
         * shortly afterwards — so the click handler only parks its trigger here
         * and this decides what to do with it.
         */
        const sample = () => {
            // A redirect drives the prototype through every screen on the way to
            // its target. Recording those would rewrite the path to the screen
            // the reviewer is actually standing on, so the walk is watched in
            // silence and the refs are left exactly as the reviewer left them.
            if (isEditorRedirecting(hostRef.current)) return

            let hash = ''
            try {
                hash = screenIdForIframe(getPrototypeHtmlIframe(hostRef.current))
            } catch {
                // iframe may be swapping documents
                return
            }
            if (!isRealScreen(hash)) return

            const prev = lastHtmlScreenIdRef.current
            if (!prev) {
                // First real screen after a load: the root of the path.
                lastHtmlScreenIdRef.current = hash
                pendingClickRef.current = null
                recordStep(null, hash)
                return
            }

            const pending = pendingClickRef.current

            if (hash !== prev) {
                fromHtmlScreenIdRef.current = prev
                lastHtmlScreenIdRef.current = hash
                // Credit the change to a click only if one just happened.
                // Anything older is coincidence, and attributing a self-advance
                // to a stale click records a step replay will fire at the wrong
                // moment.
                const credited =
                    pending && Date.now() - pending.at < CLICK_ATTRIBUTION_MS
                        ? pending.trigger
                        : null
                pendingClickRef.current = null
                recordStep(credited, hash)
                return
            }

            // Same screen, and the click has had time to prove it. Record it
            // anyway. A popup or dropdown small enough to count as floating
            // chrome is deliberately excluded from the fingerprint — opening one
            // is invisible here — so a rule of "only clicks that moved the
            // screen" drops the step that makes the NEXT one possible, and
            // replay clicks a menu item into a closed menu.
            if (pending && Date.now() - pending.at >= CLICK_SETTLE_MS) {
                pendingClickRef.current = null
                recordStep(pending.trigger, hash)
            }
        }

        const tick = sample
        tick()
        const interval = window.setInterval(tick, 250)
        const iframe = getPrototypeHtmlIframe(hostRef.current)
        const onLoad = () => window.setTimeout(tick, 80)
        iframe?.addEventListener('load', onLoad)
        return () => {
            window.clearInterval(interval)
            iframe?.removeEventListener('load', onLoad)
        }
    }, [editorSettled, drawingId, editorMountKey])

    useEffect(() => {
        if (!editorSettled || !drawingId) return undefined
        const host = hostRef.current
        if (!host) return undefined

        let iframe = null
        let doc = null
        /**
         * Offer this click to the screen watcher.
         *
         * Deliberately does not touch the path. The watcher is the only writer,
         * and it appends this trigger only if the fingerprint actually moves
         * within CLICK_ATTRIBUTION_MS — so a click that opened a menu, or landed
         * on inert chrome, never becomes a replay step.
         */
        const recordTrigger = (trigger) => {
            if (!trigger) return
            lastHtmlTriggerRef.current = trigger
            pendingClickRef.current = { trigger, at: Date.now() }
        }
        /**
         * Only a human's click describes a route.
         *
         * `isTrusted` is false for anything dispatched from script — the
         * editor's own replay (which calls `.click()`), and any click the
         * prototype fires at itself. Both used to land in the recorded path as
         * though a reviewer had made them.
         */
        const isReviewerAction = (event) =>
            event?.isTrusted !== false && !isEditorRedirecting(host)

        const onClick = (event) => {
            if (!isReviewerAction(event)) return
            // A value control fires click AND change, and recording both put the
            // same control into the path twice in a row — once with the value it
            // had before the reviewer chose or typed, and once with the one they
            // did. The click carries the stale value, so it is the one to drop:
            // `change` is where the navigation actually is.
            if (isRecordableValueControl(event.target)) return
            recordTrigger(triggerFromEventTarget(event.target))
        }
        const onChange = (event) => {
            if (!isReviewerAction(event)) return
            const target = event.target
            if (!isRecordableValueControl(target)) return
            recordTrigger(triggerFromEventTarget(target))
        }
        /**
         * A reload — or the package swapping in a buffered iframe — puts the
         * prototype back on its load screen. The recorded path must restart
         * from there, or it claims to begin at the load screen while its first
         * steps belong to a state that no longer exists.
         */
        const resetClickPath = () => {
            htmlClickPathRef.current = []
            pendingClickRef.current = null
            lastHtmlTriggerRef.current = null
            // Clearing this re-roots the path: the watcher takes its next
            // sighting as a fresh load screen and records it at index 0.
            lastHtmlScreenIdRef.current = ''
        }
        const onLoad = () => {
            // A redirect reloads to get back to the load screen before replaying.
            // That is the editor's doing, and throwing away the reviewer's
            // recorded path because of it would lose the route to wherever they
            // were standing when they clicked the comment.
            if (!isEditorRedirecting(host)) resetClickPath()
            bindDoc()
        }
        const bindDoc = () => {
            const next = getPrototypeHtmlIframe(host)
            if (!next) return
            if (iframe && next !== iframe && !isEditorRedirecting(host)) resetClickPath()
            iframe = next
            iframe.removeEventListener('load', onLoad)
            iframe.addEventListener('load', onLoad)
            try {
                doc?.removeEventListener('click', onClick, true)
                doc?.removeEventListener('change', onChange, true)
                doc = iframe.contentDocument
                doc?.addEventListener('click', onClick, true)
                doc?.addEventListener('change', onChange, true)
            } catch {
                doc = null
            }
        }
        bindDoc()
        const timer = window.setInterval(bindDoc, 1000)
        return () => {
            window.clearInterval(timer)
            iframe?.removeEventListener('load', onLoad)
            try {
                doc?.removeEventListener('click', onClick, true)
                doc?.removeEventListener('change', onChange, true)
            } catch {
                // iframe document may already be gone
            }
        }
    }, [editorSettled, drawingId, editorMountKey])

    const pinCommentsOnShapes = useCallback(
        async (list) => {
            const editor = editorRef.current
            if (!editor?.addComment) return
            const existing = new Set(
                (editor.getComments?.() || []).map((item) =>
                    String(item?.annotationId || '')
                )
            )
            pinnedCommentsRef.current.forEach((id) => existing.add(id))
            for (const item of Array.isArray(list) ? list : []) {
                const annotationId = String(item?.annotationId || '')
                const text = String(item?.text || '').trim()
                if (!annotationId || !text || item.isResolved) continue
                if (existing.has(annotationId)) continue
                try {
                    await editor.addComment({
                        author: item.author || editorUser?.name || 'User',
                        text,
                        annotationId,
                    })
                    existing.add(annotationId)
                    pinnedCommentsRef.current.add(annotationId)
                } catch {
                    // Shape may not be on the current HTML screen yet.
                }
            }
        },
        [editorUser]
    )

    useEffect(() => {
        if (!markersLive || !comments.length) return undefined
        pinCommentsOnShapes(comments)
        return undefined
    }, [markersLive, comments, pinCommentsOnShapes])

    const refreshActivity = useCallback(async () => {
        const activeId =
            versionId && versionId !== 'current'
                ? versionId
                : currentVersionId
        const activityRes = await listPrototypeActivity(prototypeId, {
            ...(activeId ? { versionId: activeId } : {}),
            skip: 0,
            limit: 100,
        })
        if (!activityRes.success) return
        setHistoryActivity(
            (activityRes.result || []).map((item, index) =>
                toHistoryActivityItem(item, index, prototypeId)
            )
        )
    }, [prototypeId, versionId, currentVersionId])

    const handleAddComment = useCallback(
        async (payload) => {
            if (readOnly) return false
            const annotationId = String(payload?.annotationId || '')
            const text = String(payload?.text || '').trim()
            const parentId = String(payload?.parentId || '')
            if (!text) return false

            if (parentId) {
                syncingCommentRef.current = true
                try {
                    const res = await addPrototypeReply(parentId, { body: text })
                    if (!res.success || !res.result) {
                        showActionNotification({
                            type: 'error',
                            message: res.message || 'Failed to add reply',
                        })
                        return false
                    }
                    const reply = toEditorReply(
                        {
                            ...res.result,
                            authorName:
                                res.result.authorName ||
                                payload.author ||
                                editorUser?.name ||
                                'User',
                            authorId:
                                res.result.authorId || editorUser?.id,
                        },
                        { id: parentId, annotationId }
                    )
                    if (!reply) return false
                    setComments((prev) =>
                        (Array.isArray(prev) ? prev : []).map((c) => {
                            if (c.id !== parentId) return c
                            const replies = [...(c.replies || []), reply]
                            return {
                                ...c,
                                replies,
                                replyCount: replies.length,
                            }
                        })
                    )
                    refreshActivity()
                    return true
                } finally {
                    window.setTimeout(() => {
                        syncingCommentRef.current = false
                    }, 400)
                }
            }

            if (!annotationId) return false

            const selected =
                selectedObjectRef.current?.annotationId === annotationId
                    ? selectedObjectRef.current
                    : selectedObjectRef.current
            const shape = shapeFromFabricObject(selected)
            if (!shape) {
                message.warning(
                    'Select a drawn shape on the canvas before commenting'
                )
                return false
            }

            // Claimed synchronously, before the first await, so a double submit
            // cannot slip between the check and the POST.
            if (creatingCommentIdsRef.current.has(annotationId)) return false
            creatingCommentIdsRef.current.add(annotationId)

            const { liveScreenId } = readLiveHtmlScreenId(hostRef.current)
            // Freeze the screen the reviewer is looking at and send it with the
            // comment. Everything else in this payload is a RECORDING of how
            // they got here, replayed later and hoping the prototype behaves;
            // this is the screen itself, so a teammate opening the comment on a
            // page they have never visited still lands on it.
            //
            // Awaited before the POST because it is gzip+base64 and therefore
            // editorRef, NOT hostRef: hostRef is the container div that
            // getPrototypeHtmlIframe() searches, and a div has no such method —
            // optional chaining then swallows the whole call and every comment
            // ships without a snapshot, with nothing logged.
            //
            // Explicit about a missing method rather than optional-chaining past
            // it: a silent undefined here is exactly the failure this line has
            // already had once, and it is invisible until someone diffs the
            // payload against the schema.
            let snapshot = null
            let snapshotSkipped = ''
            const captureSnapshot = editorRef.current?.captureScreenSnapshotForComment
            if (typeof captureSnapshot !== 'function') {
                snapshotSkipped =
                    'the editor exposes no captureScreenSnapshotForComment — ' +
                    '@scopmx/markup-editor is older than 1.23.0'
            } else {
                try {
                    snapshot = await captureSnapshot.call(editorRef.current)
                    if (!snapshot) {
                        snapshotSkipped =
                            'the editor returned nothing — the background is not an ' +
                            'HTML prototype, the iframe was unreadable, or the screen ' +
                            'had not settled'
                    }
                } catch (error) {
                    snapshot = null
                    snapshotSkipped = `capture threw: ${error?.message || error}`
                }
            }
            // Say so, specifically. A comment that saves without a snapshot
            // looks completely normal — the POST succeeds, the marker appears —
            // and the cost only shows up much later as a redirect that cannot
            // reach the screen. This line exists because exactly that went
            // unnoticed once already.
            if (snapshotSkipped && process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn(
                    `[pmx] comment saved WITHOUT a screen snapshot: ${snapshotSkipped}. ` +
                        'Redirect to this comment will fall back to click replay.'
                )
            }
            const screen = buildLiveApiCommentScreen({
                selected,
                host: hostRef.current,
                htmlScreenId: liveScreenId,
                lastTrigger: lastHtmlTriggerRef.current,
                clickChain: recordedClickChain(),
                fromScreenId: fromHtmlScreenIdRef.current,
                snapshot,
            })
            syncingCommentRef.current = true
            try {
                const res = await addPrototypeComment(prototypeId, {
                    body: text,
                    shape: toApiCommentShape(shape, annotationId),
                    screen,
                    versionId:
                        versionId && versionId !== 'current'
                            ? versionId
                            : undefined,
                })
                if (!res.success || !res.result) {
                    showActionNotification({
                        type: 'error',
                        message: res.message || 'Failed to save comment',
                    })
                    return false
                }

                const apiComment = res.result
                reportDroppedScreenFields(screen, apiComment?.screen)
                // Keep annotationId as the fabric object's id so selection
                // still lines up; store API id as the comment id.
                const editorComment = {
                    ...toEditorComment({
                        ...apiComment,
                        body: apiComment.body || text,
                        authorName:
                            apiComment.authorName ||
                            payload.author ||
                            editorUser?.name ||
                            'User',
                        authorId:
                            apiComment.authorId || editorUser?.id,
                    }),
                    annotationId,
                    id: String(apiComment._id),
                    authorId: String(
                        apiComment.authorId || editorUser?.id || ''
                    ) || null,
                    shape: toApiCommentShape(shape, annotationId),
                    ...(() => {
                        const nextScreen = toPackageCommentScreen({
                            ...(apiComment.screen || {}),
                            ...(screen || {}),
                        })
                        return nextScreen
                            ? { screen: nextScreen, htmlScreen: nextScreen }
                            : {}
                    })(),
                    replies: Array.isArray(apiComment.replies)
                        ? apiComment.replies
                              .map((item) =>
                                  toEditorReply(item, {
                                      id: String(apiComment._id),
                                      annotationId,
                                  })
                              )
                              .filter(Boolean)
                        : [],
                }
                if (!editorComment.id) return false

                setComments((prev) => {
                    const withoutDup = (Array.isArray(prev) ? prev : []).filter(
                        (c) =>
                            c.id !== editorComment.id &&
                            !(
                                c.annotationId === annotationId &&
                                c.text === text
                            )
                    )
                    return [editorComment, ...withoutDup]
                })
                pinnedCommentsRef.current.add(annotationId)
                try {
                    await editorRef.current?.addComment?.({
                        author:
                            editorComment.author ||
                            payload.author ||
                            editorUser?.name ||
                            'User',
                        text,
                        annotationId,
                    })
                } catch {
                    // Canvas marker is best-effort; the comment is already saved.
                }
                refreshActivity()
                return true
            } finally {
                creatingCommentIdsRef.current.delete(annotationId)
                window.setTimeout(() => {
                    syncingCommentRef.current = false
                }, 400)
            }
        },
        [
            prototypeId,
            versionId,
            currentVersionId,
            drawingId,
            editorUser,
            recordedClickChain,
            refreshActivity,
            readOnly,
        ]
    )

    const handleSelectComment = useCallback((annotationId) => {
        editorRef.current?.selectComment(annotationId)
    }, [])

    const handleRightTabChange = useCallback((tab) => {
        setRightTab(tab === 'comments' ? HOST_COMMENTS_TAB : tab)
    }, [])

    const handleEditComment = useCallback(
        async (payload) => {
            if (readOnly) return false
            const commentId = String(payload?.id || '')
            const body = String(payload?.text || '').trim()
            if (!commentId || !body) return false
            const list = commentsRef.current || []
            const comment = list.find((item) => item.id === commentId)
            const parent = comment
                ? null
                : list.find((item) =>
                      (item.replies || []).some((reply) => reply.id === commentId)
                  )
            const reply = parent?.replies?.find((item) => item.id === commentId)
            const target = comment || reply
            if (target && !isSuperAdmin && !isSameUser(editorUser, target))
                return false
            const res = await updatePrototypeComment(commentId, { body })
            if (!res.success) {
                showActionNotification({
                    type: 'error',
                    message: res.message || 'Failed to update comment',
                })
                return false
            }
            const updatedAt =
                res.result?.updatedAt || new Date().toISOString()
            setComments((prev) =>
                (Array.isArray(prev) ? prev : []).map((c) => {
                    if (c.id === commentId) {
                        return { ...c, text: body, updatedAt }
                    }
                    const replies = (c.replies || []).map((r) =>
                        r.id === commentId
                            ? { ...r, text: body, updatedAt }
                            : r
                    )
                    return { ...c, replies }
                })
            )
            refreshActivity()
            showActionNotification({
                type: 'success',
                message: res.message || 'Comment updated',
            })
            return true
        },
        [readOnly, isSuperAdmin, editorUser, refreshActivity]
    )

    const handleAnnotationMoved = useCallback(
        async ({ annotationId, from, to, toolId, htmlScreenId }) => {
            if (readOnly) return
            const id = String(annotationId || '')
            if (!id) return
            const linked = commentsLinkedToAnnotations(commentsRef.current, [
                id,
            ])
            if (!linked.length) return

            const selected =
                String(selectedObjectRef.current?.annotationId || '') === id
                    ? selectedObjectRef.current
                    : null
            const fromFabric = shapeFromFabricObject(selected)
            const { liveScreenId } = readLiveHtmlScreenId(hostRef.current)
            const nextHash = String(htmlScreenId || liveScreenId || '').trim()

            const results = await Promise.all(
                linked.map(async (comment) => {
                    const shape = toApiCommentShape(
                        fromFabric ||
                            applyMovedBoxToShape(
                                comment.shape,
                                from,
                                to,
                                id,
                                toolId
                            ),
                        id
                    )
                    const previousHash = String(comment.screen?.hash || '')
                    const fromScreenId =
                        previousHash && previousHash !== nextHash
                            ? previousHash
                            : fromHtmlScreenIdRef.current
                    const screen = buildLiveApiCommentScreen({
                        selected,
                        host: hostRef.current,
                        htmlScreenId: nextHash,
                        lastTrigger: lastHtmlTriggerRef.current,
                        clickChain: recordedClickChain(),
                        fromScreenId,
                        // Moving a mark rebuilds the screen from whatever the
                        // live host reports, which on a freshly reloaded
                        // prototype is nothing. Without the stored screen
                        // underneath, a drag sends trigger:null, chain:[] and
                        // no signature or snapshot, replacing a working route
                        // with an empty one. This is the frontend half of the
                        // rule the API follows on update: absent means "leave
                        // what is stored", never "clear it".
                        previousScreen: comment.screen || comment.htmlScreen,
                    })
                    const res = await updatePrototypeComment(comment.id, {
                        ...(shape ? { shape } : {}),
                        ...(screen ? { screen } : {}),
                    })
                    // Same round-trip check the create path runs, but only when
                    // the reply actually carried a screen: an update response is
                    // not guaranteed to echo one, and an absent screen would
                    // otherwise read as every field having been dropped.
                    if (screen && res.result?.screen) {
                        reportDroppedScreenFields(screen, res.result.screen)
                    }
                    return { comment, res, shape, screen }
                })
            )

            const failed = results.find((item) => !item.res.success)
            if (failed) {
                showActionNotification({
                    type: 'error',
                    message:
                        failed.res.message ||
                        'Failed to update comment position',
                })
            }

            const succeeded = results.filter((item) => item.res.success)
            if (!succeeded.length) return

            setComments((prev) =>
                (Array.isArray(prev) ? prev : []).map((comment) => {
                    const match = succeeded.find(
                        (item) => item.comment.id === comment.id
                    )
                    if (!match) return comment
                    const nextScreen = toPackageCommentScreen(match.screen)
                    return {
                        ...comment,
                        shape: match.shape,
                        ...(nextScreen
                            ? {
                                  screen: nextScreen,
                                  htmlScreen: nextScreen,
                              }
                            : {}),
                        updatedAt:
                            match.res.result?.updatedAt ||
                            new Date().toISOString(),
                    }
                })
            )
            refreshActivity()
        },
        [readOnly, recordedClickChain, refreshActivity]
    )

    const canManageComment = useCallback(
        (item) => {
            if (readOnly || !item) return false
            if (isSuperAdmin) return true
            return isSameUser(editorUser, item)
        },
        [readOnly, isSuperAdmin, editorUser]
    )

    const startEditComment = useCallback((item) => {
        if (!canManageComment(item) || !item?.id) return
        setReplyOpenId(null)
        setEditingId(item.id)
        setEditDraft(item.text || '')
    }, [canManageComment])

    const cancelEditComment = useCallback(() => {
        setEditingId(null)
        setEditDraft('')
    }, [])

    const saveEditComment = useCallback(async () => {
        const commentId = String(editingId || '')
        const body = String(editDraft || '').trim()
        if (!commentId || !body) {
            message.warning('Write a comment first')
            return
        }
        setBusyCommentId(commentId)
        try {
            const ok = await handleEditComment({ id: commentId, text: body })
            if (ok) {
                setEditingId(null)
                setEditDraft('')
            }
        } finally {
            setBusyCommentId(null)
        }
    }, [editingId, editDraft, handleEditComment])

    const handleSubmitNewComment = useCallback(async () => {
        if (readOnly) return
        const text = String(commentDraft || '').trim()
        const annotationId = String(selectedAnnotationId || '')
        if (!text || !annotationId) {
            message.warning('Select a shape, then write a comment')
            return
        }
        const ok = await handleAddComment({
            author: editorUser?.name || 'User',
            text,
            annotationId,
        })
        if (ok) setCommentDraft('')
    }, [
        readOnly,
        commentDraft,
        selectedAnnotationId,
        handleAddComment,
        editorUser,
    ])

    const handleDeleteComment = useCallback(
        (comment) => {
            if (!canManageComment(comment) || !comment?.id) return
            setDeleteTarget({ type: 'comment', comment })
        },
        [canManageComment]
    )

    const handleToggleResolve = useCallback(
        async (comment) => {
            if (!canManageComment(comment) || !comment?.id) return
            const nextResolved = !comment.isResolved
            setBusyCommentId(comment.id)
            try {
                const res = await resolvePrototypeComment(
                    comment.id,
                    nextResolved
                )
                if (!res.success) {
                    showActionNotification({
                        type: 'error',
                        message:
                            res.message ||
                            (nextResolved
                                ? 'Failed to resolve comment'
                                : 'Failed to reopen comment'),
                    })
                    return
                }
                setComments((prev) =>
                    (Array.isArray(prev) ? prev : []).map((c) =>
                        c.id === comment.id
                            ? {
                                  ...c,
                                  isResolved: Boolean(
                                      res.result?.isResolved ?? nextResolved
                                  ),
                              }
                            : c
                    )
                )
                message.success(
                    res.message ||
                        (nextResolved ? 'Comment resolved' : 'Comment reopened')
                )
                refreshActivity()
            } finally {
                setBusyCommentId(null)
            }
        },
        [refreshActivity, canManageComment]
    )

    const handleSubmitReply = useCallback(
        async (comment) => {
            if (readOnly) return
            if (!comment?.id) return
            const text = String(replyDrafts[comment.id] || '').trim()
            if (!text) {
                message.warning('Write a reply first')
                return
            }
            setBusyCommentId(comment.id)
            try {
                const res = await addPrototypeReply(comment.id, { body: text })
                if (!res.success || !res.result) {
                    showActionNotification({
                        type: 'error',
                        message: res.message || 'Failed to add reply',
                    })
                    return
                }
                const reply = toEditorReply({
                    ...res.result,
                    authorName:
                        res.result.authorName || editorUser?.name || 'User',
                    authorId: res.result.authorId || editorUser?.id,
                })
                if (!reply) return
                setComments((prev) =>
                    (Array.isArray(prev) ? prev : []).map((c) => {
                        if (c.id !== comment.id) return c
                        const replies = [...(c.replies || []), reply]
                        return {
                            ...c,
                            replies,
                            replyCount: replies.length,
                        }
                    })
                )
                setReplyDrafts((prev) => ({ ...prev, [comment.id]: '' }))
                setReplyOpenId(null)
                message.success(res.message || 'Reply added')
                refreshActivity()
            } finally {
                setBusyCommentId(null)
            }
        },
        [replyDrafts, editorUser, refreshActivity, readOnly]
    )

    const handleDeleteReply = useCallback(
        (comment, reply) => {
            if (!canManageComment(reply) || !comment?.id || !reply?.id) return
            setDeleteTarget({ type: 'reply', comment, reply })
        },
        [canManageComment]
    )

    const closeDeletePopup = useCallback(() => {
        if (deleteLoading) return
        if (deleteTarget?.type === 'shape' && shapeDeleteConfirmRef.current) {
            shapeDeleteConfirmRef.current(false)
            shapeDeleteConfirmRef.current = null
        }
        setDeleteTarget(null)
        setDeleteLoading(false)
    }, [deleteLoading, deleteTarget])

    const requestShapeDeleteConfirm = useCallback((shapeCount, commentCount) => {
        return new Promise((resolve) => {
            shapeDeleteConfirmRef.current = resolve
            setDeleteTarget({ type: 'shape', shapeCount, commentCount })
        })
    }, [])

    const removeCommentShape = useCallback(
        async (annotationId) => {
            const id = String(annotationId || '')
            const editor = editorRef.current
            if (!id || !editor?.selectAnnotation) return false

            const host = hostRef.current
            exitHtmlBrowseMode(host)
            hostDeletedAnnotationIdsRef.current.add(id)

            applyCommentHtmlScreensToCanvas(
                host,
                commentsRef.current,
                selectedObjectRef.current
            )
            editor.selectAnnotation(id)
            const started = Date.now()
            while (Date.now() - started < 4500) {
                if (String(selectedAnnotationIdRef.current || '') === id) break
                await waitMs(80)
            }

            const deleteBtn = host?.querySelector?.(
                '[aria-label="Delete selected"]:not(:disabled)'
            )
            if (deleteBtn instanceof HTMLElement) {
                deleteBtn.click()
            } else {
                window.dispatchEvent(
                    new KeyboardEvent('keydown', {
                        key: 'Delete',
                        code: 'Delete',
                        bubbles: true,
                        cancelable: true,
                    })
                )
            }

            await waitMs(120)
            try {
                await editor.saveVersion?.('Removed comment shape')
            } catch {
                // IndexedDB auto-save will retry
            }
            return true
        },
        []
    )

    const confirmDeleteTarget = useCallback(async () => {
        if (!deleteTarget) return
        if (deleteTarget.type === 'shape') {
            shapeDeleteConfirmRef.current?.(true)
            shapeDeleteConfirmRef.current = null
            setDeleteTarget(null)
            return
        }
        const { type, comment, reply } = deleteTarget
        setDeleteLoading(true)
        setBusyCommentId(comment.id)
        try {
            if (type === 'comment') {
                const res = await deletePrototypeComment(comment.id)
                if (!res.success) {
                    showActionNotification({
                        type: 'error',
                        message: res.message || 'Failed to delete comment',
                    })
                    return
                }
                const annotationId = String(
                    comment.annotationId || comment.id || ''
                )
                const nextComments = (
                    Array.isArray(commentsRef.current)
                        ? commentsRef.current
                        : []
                ).filter((c) => c.id !== comment.id)
                commentsRef.current = nextComments
                setComments(nextComments)

                // Only remove the shape when no other comments still use it.
                const stillLinked = nextComments.some(
                    (c) => String(c.annotationId || '') === annotationId
                )
                if (!stillLinked) {
                    await removeCommentShape(annotationId, comment)
                }

                if (
                    selectedAnnotationIdRef.current === comment.annotationId ||
                    selectedAnnotationIdRef.current === comment.id
                ) {
                    selectedAnnotationIdRef.current = null
                    setSelectedAnnotationId(null)
                }
                if (replyOpenId === comment.id) setReplyOpenId(null)
                showActionNotification({
                    type: 'success',
                    message: res.message || 'Comment deleted',
                })
                refreshActivity()
            } else if (type === 'reply' && reply?.id) {
                const res = await deletePrototypeReply(comment.id, reply.id)
                if (!res.success) {
                    showActionNotification({
                        type: 'error',
                        message: res.message || 'Failed to delete reply',
                    })
                    return
                }
                setComments((prev) =>
                    (Array.isArray(prev) ? prev : []).map((c) => {
                        if (c.id !== comment.id) return c
                        const replies = (c.replies || []).filter(
                            (r) => r.id !== reply.id
                        )
                        return {
                            ...c,
                            replies,
                            replyCount: replies.length,
                        }
                    })
                )
                showActionNotification({
                    type: 'success',
                    message: res.message || 'Reply deleted',
                })
                refreshActivity()
            }
            setDeleteTarget(null)
        } finally {
            setDeleteLoading(false)
            setBusyCommentId(null)
        }
    }, [deleteTarget, replyOpenId, refreshActivity, removeCommentShape])

    const dropLinkedComments = useCallback(async (annotationIds) => {
        const linked = commentsLinkedToAnnotations(
            commentsRef.current,
            annotationIds
        )
        if (!linked.length) return true
        const results = await Promise.all(
            linked.map((comment) => deletePrototypeComment(comment.id))
        )
        const deletedIds = new Set()
        let failed = null
        linked.forEach((comment, index) => {
            if (results[index]?.success) deletedIds.add(comment.id)
            else if (!failed) failed = results[index]
        })
        if (deletedIds.size) {
            const nextComments = (
                Array.isArray(commentsRef.current) ? commentsRef.current : []
            ).filter((item) => !deletedIds.has(item.id))
            commentsRef.current = nextComments
            setComments(nextComments)
            const selected = String(selectedAnnotationIdRef.current || '')
            if (
                annotationIdList(annotationIds).includes(selected) ||
                deletedIds.has(selected)
            ) {
                selectedAnnotationIdRef.current = null
                setSelectedAnnotationId(null)
            }
        }
        if (failed) {
            showActionNotification({
                type: 'error',
                message: failed.message || 'Failed to delete comment',
            })
            return false
        }
        return true
    }, [])

    const handleBeforeDelete = useCallback(
        async (annotationIds) => {
            if (readOnly) return false
            const ids = annotationIdList(annotationIds)
            if (
                ids.length &&
                ids.every((id) => hostDeletedAnnotationIdsRef.current.has(id))
            ) {
                return true
            }
            const linked = commentsLinkedToAnnotations(
                commentsRef.current,
                ids
            )
            if (linked.length) {
                const confirmed = await requestShapeDeleteConfirm(
                    ids.length,
                    linked.length
                )
                if (!confirmed) return false
                const saved = await dropLinkedComments(ids)
                if (!saved) return false
            }
            ids.forEach((id) => hostDeletedAnnotationIdsRef.current.add(id))
            return true
        },
        [dropLinkedComments, readOnly, requestShapeDeleteConfirm]
    )

    const handleAnnotationsRemoved = useCallback(
        async (annotationIds) => {
            const ids = annotationIdList(annotationIds)
            const alreadyRemoved = ids.filter((id) =>
                hostDeletedAnnotationIdsRef.current.has(id)
            )
            alreadyRemoved.forEach((id) =>
                hostDeletedAnnotationIdsRef.current.delete(id)
            )
            const leftover = ids.filter(
                (id) => !alreadyRemoved.includes(id)
            )
            if (leftover.length && !readOnly) {
                await dropLinkedComments(leftover)
            }
            try {
                await editorRef.current?.saveVersion?.('Removed annotation')
            } catch {
                // IndexedDB auto-save will retry
            }
            refreshActivity()
            refreshCanvasHistory()
        },
        [
            dropLinkedComments,
            readOnly,
            refreshActivity,
            refreshCanvasHistory,
        ]
    )

    const handleRestoreCanvasVersion = useCallback(
        async (nextVersionId) => {
            if (readOnly || !nextVersionId) return
            setRestoringVersionId(nextVersionId)
            try {
                await editorRef.current?.restoreVersionById?.(nextVersionId)
                await refreshCanvasHistory()
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message:
                        error?.message || 'Failed to restore drawing version',
                })
            } finally {
                setRestoringVersionId(null)
            }
        },
        [readOnly, refreshCanvasHistory]
    )

    const handleUploadVersion = async (file) => {
        if (readOnly) return
        if (!file || !isHtmlFile(file)) {
            message.warning('Please choose an .html file')
            return
        }
        // setUploadingVersion(true)
        const uploaded = await uploadPrototypeHtmlFile(file)
        if (!uploaded.success) {
            // setUploadingVersion(false)
            showActionNotification({
                type: 'error',
                message: uploaded.message || 'Upload failed',
            })
            return
        }
        const res = await uploadPrototypeVersion(prototypeId, {
            fileUrl: uploaded.publicLink,
            fileName: uploaded.fileName,
            fileSize: uploaded.fileSize,
            carryComments: true,
        })
        // setUploadingVersion(false)
        if (!res.success) {
            showActionNotification({
                type: 'error',
                message: res.message || 'Failed to upload version',
            })
            return
        }
        showActionNotification({
            type: 'success',
            message: res.message || 'Version uploaded',
        })
        // Clear cache so the new HTML loads into a fresh drawing.
        void clearDrawingIdForPrototype(
            prototypeId,
            res.result?._id || detail?.currentVersionId || 'current'
        )
        await load()
    }

   /*  const handleSwitchVersion = useCallback(
        async (nextVersionId) => {
            if (readOnly) return
            const targetId = String(nextVersionId || '')
            if (!targetId) return
            const activeId = String(
                versionId || currentVersionId || detail?.currentVersionId || ''
            )
            if (targetId === activeId || switchingVersionId) return

            setSwitchingVersionId(targetId)
            // setRightTab('html-versions')
            try {
                const res = await activatePrototypeVersion(
                    prototypeId,
                    targetId
                )
                if (!res.success) {
                    showActionNotification({
                        type: 'error',
                        message: res.message || 'Failed to switch version',
                        zIndex: NOTIFICATION_Z_ABOVE_FULLSCREEN,
                    })
                    return
                }
                void clearDrawingIdForPrototype(prototypeId, targetId)
                void clearDrawingIdForPrototype(
                    prototypeId,
                    activeId || detail?.currentVersionId || 'current'
                )
                await load()
                showActionNotification({
                    type: 'success',
                    message: res.message || 'Switched to selected version',
                    zIndex: NOTIFICATION_Z_ABOVE_FULLSCREEN,
                })
            } finally {
                setSwitchingVersionId(null)
            }
        },
        [
            versionId,
            currentVersionId,
            detail?.currentVersionId,
            switchingVersionId,
            prototypeId,
            load,
            readOnly,
        ]
    ) */

    useEffect(() => {
        let timer = null
        let retryTimer = null

        const attachToolbar = () => {
            const slot = attachTabBarSlot({
                hostAttr: PROTOTYPE_TAB_BAR_HOST_ATTR,
                slotAttr: PROTOTYPE_TAB_BAR_SLOT_ATTR,
                hostClassName: styles.tabBarHost,
            })
            if (!slot) return false
            setTabBarSlot(slot)
            return true
        }

        if (!attachToolbar()) {
            timer = window.setTimeout(attachToolbar, 0)
            retryTimer = window.setTimeout(attachToolbar, 120)
        }

        return () => {
            if (timer) window.clearTimeout(timer)
            if (retryTimer) window.clearTimeout(retryTimer)
            removeTabBarHost(PROTOTYPE_TAB_BAR_HOST_ATTR)
            setTabBarSlot(null)
        }
    }, [prototypeId])

    const handleDeleteCommentFromPackage = useCallback(
        (payload) => {
            if (readOnly) return
            const commentId = String(payload?.id || '')
            if (!commentId) return
            const list = commentsRef.current || []
            const comment = list.find((item) => item.id === commentId)
            if (comment) {
                if (!canManageComment(comment)) return
                setDeleteTarget({ type: 'comment', comment })
                return
            }
            const parent = list.find((item) =>
                (item.replies || []).some((reply) => reply.id === commentId)
            )
            const reply = parent?.replies?.find((item) => item.id === commentId)
            if (parent && reply) {
                if (!canManageComment(reply)) return
                setDeleteTarget({ type: 'reply', comment: parent, reply })
            }
        },
        [readOnly, canManageComment]
    )

    const handleExportComments = useCallback(async () => {
        if (isClientUser || !prototypeId || exportingComments) return

        setExportingComments(true)
        const activeVersionId =
            versionId || currentVersionId || detail?.currentVersionId || ''
        const safeName = String(detail?.name || 'Prototype')
            .replace(/[^a-zA-Z0-9]+/g, '_')
            .replace(/^_+|_+$/g, '')
        const result = await exportPrototypeComments(prototypeId, {
            ...(activeVersionId ? { versionId: activeVersionId } : {}),
            fileName: safeName
                ? `${safeName}_Comments.xlsx`
                : undefined,
        })
        setExportingComments(false)

        if (!result.success) {
            showActionNotification({
                type: 'error',
                message: result.message || 'Failed to export comments',
            })
            return
        }

        showActionNotification({
            type: 'success',
            message: result.message || 'Comments exported successfully',
        })
    }, [
        isClientUser,
        prototypeId,
        exportingComments,
        versionId,
        currentVersionId,
        detail?.currentVersionId,
        detail?.name,
    ])

    const commentsPanelProps = useMemo(
        () => {
            const threads = Array.isArray(comments) ? comments : []
            return {
                comments: toPackageCommentRows(threads),
                selectedObject,
                selectedAnnotationId,
                annotationLabels: threads.reduce((acc, item) => {
                    const annotationId = String(item.annotationId || '')
                    if (annotationId && item.elementLabel) {
                        acc[annotationId] = item.elementLabel
                    }
                    return acc
                }, {}),
                onAddComment: handleAddComment,
                onEditComment: handleEditComment,
                onDeleteComment: handleDeleteCommentFromPackage,
                onSelectComment: handleSelectComment,
            }
        },
        [
            comments,
            selectedObject,
            selectedAnnotationId,
            handleAddComment,
            handleEditComment,
            handleDeleteCommentFromPackage,
            handleSelectComment,
        ]
    )

    const historyPanelProps = useMemo(
        () => {
            const currentUserId = String(editorUser?.id || '')
            const currentUserName = String(editorUser?.name || '')
                .trim()
                .toLowerCase()
            const isCurrentUser = (userId, userName) => {
                if (currentUserId && String(userId || '') === currentUserId) {
                    return true
                }
                const name = String(userName || '').trim().toLowerCase()
                return Boolean(currentUserName && name && name === currentUserName)
            }
            const activity = (Array.isArray(historyActivity)
                ? historyActivity
                : []
            ).map((item) => ({
                id: item.id,
                drawingId: item.drawingId,
                versionId: null,
                userId: item.userId,
                userName: isCurrentUser(item.userId, item.userName)
                    ? 'You'
                    : item.userName,
                action: item.action,
                summary: item.summary || item.title || item.text || '',
                details: item.data || null,
                createdAt: item.createdAt,
            }))
            const usersById = new Map()
            if (editorUser?.id) {
                usersById.set(String(editorUser.id), {
                    id: String(editorUser.id),
                    name: 'You',
                })
            }
            activity.forEach((item) => {
                const id = String(item.userId || '')
                if (id && !usersById.has(id)) {
                    usersById.set(id, {
                        id,
                        name: item.userName || 'User',
                    })
                }
            })
            return {
                versions: [],
                activity,
                users: [...usersById.values()],
                restoringVersionId,
                onRestoreVersion: handleRestoreCanvasVersion,
            }
        },
        [historyActivity, editorUser, restoringVersionId, handleRestoreCanvasVersion]
    )

    /* const htmlVersionsPanel = (
        <div className={styles.sideBody}>
            {!readOnly ? (
                <button
                    type="button"
                    className={styles.sidePrimaryBtn}
                    disabled={uploadingVersion}
                    onClick={() => versionInputRef.current?.click()}
                    title="Upload new version"
                >
                    {uploadingVersion ? (
                        <Spin size="small" />
                    ) : (
                        <Icon icon="mdi:upload" width={16} height={16} />
                    )}
                    <span>
                        {uploadingVersion ? 'Uploading…' : 'New version'}
                    </span>
                </button>
            ) : null}
            {historyVersions.length === 0 ? (
                <p className={styles.sideMuted}>No HTML versions yet.</p>
            ) : (
                <ul className={styles.historyList}>
                    {historyVersions.map((item) => {
                        const activeId = String(
                            versionId || currentVersionId || ''
                        )
                        const isActive = item.id === activeId
                        const isBusy = switchingVersionId === item.id
                        return (
                            <li key={item.id}>
                                <button
                                    type="button"
                                    className={`${styles.versionItem} ${
                                        isActive ? styles.versionItemActive : ''
                                    }`}
                                    disabled={
                                        readOnly ||
                                        Boolean(switchingVersionId) ||
                                        isActive
                                    }
                                    onClick={() =>
                                        handleSwitchVersion(item.id)
                                    }
                                    title={
                                        isActive
                                            ? 'Current version'
                                            : readOnly
                                              ? `Version ${item.versionNumber}`
                                              : `Switch to version ${item.versionNumber}`
                                    }
                                >
                                    <div className={styles.historyRowTop}>
                                        <strong>
                                            Version {item.versionNumber}
                                        </strong>
                                        <span className={styles.versionBadges}>
                                            {isBusy ? (
                                                <Spin size="small" />
                                            ) : null}
                                            {isActive || item.isCurrent ? (
                                                <span
                                                    className={
                                                        styles.versionBadge
                                                    }
                                                >
                                                    Current
                                                </span>
                                            ) : !readOnly ? (
                                                <span
                                                    className={
                                                        styles.versionSwitchHint
                                                    }
                                                >
                                                    Switch
                                                </span>
                                            ) : null}
                                        </span>
                                    </div>
                                    {item.fileName ? (
                                        <span className={styles.historyDetail}>
                                            {item.fileName}
                                        </span>
                                    ) : null}
                                    {item.changeNote ? (
                                        <span className={styles.historyDetail}>
                                            {item.changeNote}
                                        </span>
                                    ) : null}
                                    <span>
                                        {item.createdBy?.userName}
                                        {item.createdBy?.userName ? ' · ' : ''}
                                        {formatDateTime(item.createdAt)}
                                    </span>
                                </button>
                            </li>
                        )
                    })}
                </ul>
            )}
        </div>
    ) */

    const commentsPanel = (
        <div className={`${styles.sideBody} ${styles.commentsTabHost}`}>
            <div className={styles.commentsPanelHeader}>
                <div>
                    <h3 className={styles.commentsPanelTitle}>Comments</h3>
                    <p className={styles.commentsPanelSubtitle}>
                        {selectedAnnotationId
                            ? 'Add a note on the selected shape, or manage replies below.'
                            : 'Draw and select a shape to add a comment. Click a thread to open its page.'}
                    </p>
                </div>
                <div className={styles.commentsPanelHeaderActions}>
                    {!isClientUser ? (
                        <button
                            type="button"
                            className={styles.ghostButton}
                            disabled={exportingComments}
                            onClick={handleExportComments}
                            title="Export comments"
                        >
                            {exportingComments ? (
                                <Spin size="small" />
                            ) : (
                                <Icon
                                    icon="mdi:download"
                                    width={14}
                                    height={14}
                                />
                            )}
                            <span>
                                {exportingComments ? 'Exporting…' : 'Export'}
                            </span>
                        </button>
                    ) : null}
                    <span className={styles.commentsCountBadge}>
                        {comments.length}
                    </span>
                </div>
            </div>

            {!readOnly ? (
                selectedAnnotationId ? (
                    <div className={styles.commentComposer}>
                        <div className={styles.commentComposerMeta}>
                            <span className={styles.commentComposerBadge}>
                                Selected shape
                            </span>
                            <span className={styles.commentComposerHint}>
                                Commenting as {editorUser?.name || 'User'}
                            </span>
                        </div>
                        <TextArea
                            value={commentDraft}
                            onChange={(e) => setCommentDraft(e.target.value)}
                            placeholder="Write a comment for this shape…"
                            rows={3}
                            maxLength={2000}
                        />
                        <button
                            type="button"
                            className={styles.sidePrimaryBtn}
                            disabled={!commentDraft.trim()}
                            onClick={handleSubmitNewComment}
                        >
                            Add comment
                        </button>
                    </div>
                ) : (
                    <div className={styles.commentsEmptyCard}>
                        <Icon
                            icon="mdi:vector-square"
                            width={20}
                            height={20}
                        />
                        <strong>Select a shape to comment</strong>
                        <span>
                            Use the tools to draw a shape on the prototype,
                            then add your note here.
                        </span>
                    </div>
                )
            ) : null}

            <div className={styles.commentList}>
                {comments.length === 0 ? (
                    <p className={styles.sideMuted}>No comments yet.</p>
                ) : (
                    comments.map((item) => {
                        const isSelected =
                            selectedAnnotationId === item.annotationId ||
                            selectedAnnotationId === item.id
                        const isBusy = Boolean(busyCommentId) &&
                            (busyCommentId === item.id ||
                                busyCommentId === editingId)
                        const canEditItem = canManageComment(item)
                        const canDeleteItem = canManageComment(item)
                        const isEditingComment = editingId === item.id
                        const isReplyOpen = replyOpenId === item.id
                        const replies = Array.isArray(item.replies)
                            ? item.replies
                            : []
                        return (
                            <div
                                key={item.id}
                                className={`${styles.commentCard} ${
                                    isSelected ? styles.commentCardActive : ''
                                } ${
                                    item.isResolved
                                        ? styles.commentCardResolved
                                        : ''
                                }`}
                            >
                                <button
                                    type="button"
                                    className={styles.commentCardMain}
                                    onClick={() =>
                                        handleSelectComment(
                                            item.annotationId || item.id
                                        )
                                    }
                                >
                                    <div className={styles.commentCardTop}>
                                        <strong>{item.author || 'User'}</strong>
                                        <span>
                                            {formatDateTime(item.createdAt)}
                                        </span>
                                    </div>
                                    {editingId === item.id ? null : (
                                        <p>{item.text}</p>
                                    )}
                                    {item.isResolved ? (
                                        <span className={styles.resolvedBadge}>
                                            Resolved
                                        </span>
                                    ) : null}
                                </button>

                                {editingId === item.id && !readOnly ? (
                                    <div className={styles.replyComposer}>
                                        <TextArea
                                            value={editDraft}
                                            onChange={(e) =>
                                                setEditDraft(e.target.value)
                                            }
                                            placeholder="Edit comment…"
                                            rows={3}
                                            maxLength={2000}
                                        />
                                        <div
                                            className={
                                                styles.replyComposerActions
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={
                                                    styles.commentActionBtn
                                                }
                                                disabled={isBusy}
                                                onClick={cancelEditComment}
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    styles.sidePrimaryBtn
                                                }
                                                disabled={
                                                    isBusy ||
                                                    !editDraft.trim()
                                                }
                                                onClick={saveEditComment}
                                            >
                                                {isBusy
                                                    ? 'Saving…'
                                                    : 'Save'}
                                            </button>
                                        </div>
                                    </div>
                                ) : null}

                                {replies.length > 0 ? (
                                    <div className={styles.replyList}>
                                        {replies.map((reply) => (
                                            <div
                                                key={reply.id}
                                                className={styles.replyItem}
                                            >
                                                <div
                                                    className={
                                                        styles.replyItemTop
                                                    }
                                                >
                                                    <strong>
                                                        {reply.author ||
                                                            'User'}
                                                    </strong>
                                                    <span>
                                                        {formatDateTime(
                                                            reply.createdAt
                                                        )}
                                                    </span>
                                                </div>
                                                {editingId === reply.id ? (
                                                    <div
                                                        className={
                                                            styles.replyEditComposer
                                                        }
                                                    >
                                                        <TextArea
                                                            value={editDraft}
                                                            onChange={(e) =>
                                                                setEditDraft(
                                                                    e.target
                                                                        .value
                                                                )
                                                            }
                                                            rows={2}
                                                            maxLength={2000}
                                                        />
                                                        <div
                                                            className={
                                                                styles.replyComposerActions
                                                            }
                                                        >
                                                            <button
                                                                type="button"
                                                                className={
                                                                    styles.commentActionBtn
                                                                }
                                                                disabled={
                                                                    isBusy
                                                                }
                                                                onClick={
                                                                    cancelEditComment
                                                                }
                                                            >
                                                                Cancel
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className={
                                                                    styles.sidePrimaryBtn
                                                                }
                                                                disabled={
                                                                    isBusy ||
                                                                    !editDraft.trim()
                                                                }
                                                                onClick={
                                                                    saveEditComment
                                                                }
                                                            >
                                                                {isBusy
                                                                    ? 'Saving…'
                                                                    : 'Save'}
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <>
                                                        <p>{reply.text}</p>
                                                        {(canManageComment(reply)) ? (
                                                            <div
                                                                className={
                                                                    styles.replyItemActions
                                                                }
                                                            >
                                                                <Tooltip title="Edit">
                                                                    <button
                                                                        type="button"
                                                                        className={
                                                                            styles.replyActionBtn
                                                                        }
                                                                        aria-label="Edit"
                                                                        disabled={
                                                                            isBusy
                                                                        }
                                                                        onClick={() =>
                                                                            startEditComment(
                                                                                reply
                                                                            )
                                                                        }
                                                                    >
                                                                        <Icon
                                                                            icon="mdi:pencil-outline"
                                                                            width={
                                                                                14
                                                                            }
                                                                        />
                                                                    </button>
                                                                </Tooltip>
                                                                <Tooltip title="Delete">
                                                                    <button
                                                                        type="button"
                                                                        className={`${styles.replyActionBtn} ${styles.replyActionDanger}`}
                                                                        aria-label="Delete"
                                                                        disabled={
                                                                            isBusy
                                                                        }
                                                                        onClick={() =>
                                                                            handleDeleteReply(
                                                                                item,
                                                                                reply
                                                                            )
                                                                        }
                                                                    >
                                                                        <Icon
                                                                            icon="mdi:trash-can-outline"
                                                                            width={
                                                                                14
                                                                            }
                                                                        />
                                                                    </button>
                                                                </Tooltip>
                                                            </div>
                                                        ) : null}
                                                    </>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ) : null}

                                {isReplyOpen && !readOnly ? (
                                    <div className={styles.replyComposer}>
                                        <TextArea
                                            value={
                                                replyDrafts[item.id] || ''
                                            }
                                            onChange={(e) =>
                                                setReplyDrafts((prev) => ({
                                                    ...prev,
                                                    [item.id]: e.target.value,
                                                }))
                                            }
                                            placeholder="Write a reply…"
                                            rows={2}
                                            maxLength={2000}
                                        />
                                        <div
                                            className={
                                                styles.replyComposerActions
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={
                                                    styles.commentActionBtn
                                                }
                                                disabled={isBusy}
                                                onClick={() =>
                                                    setReplyOpenId(null)
                                                }
                                            >
                                                Cancel
                                            </button>
                                            <button
                                                type="button"
                                                className={
                                                    styles.sidePrimaryBtn
                                                }
                                                disabled={
                                                    isBusy ||
                                                    !(
                                                        replyDrafts[item.id] ||
                                                        ''
                                                    ).trim()
                                                }
                                                onClick={() =>
                                                    handleSubmitReply(item)
                                                }
                                            >
                                                {isBusy ? 'Saving…' : 'Reply'}
                                            </button>
                                        </div>
                                    </div>
                                ) : null}

                                {!isEditingComment ? (
                                <div className={styles.commentActions}>
                                    {canEditItem ? (
                                        <Tooltip title="Edit">
                                            <button
                                                type="button"
                                                className={`${styles.commentActionBtn} ${styles.commentActionIconBtn}`}
                                                aria-label="Edit"
                                                disabled={isBusy}
                                                onClick={() =>
                                                    startEditComment(item)
                                                }
                                            >
                                                <Icon
                                                    icon="mdi:pencil-outline"
                                                    width={14}
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                    {!readOnly ? (
                                        <Tooltip
                                            title={
                                                replies.length
                                                    ? `Reply (${replies.length})`
                                                    : 'Reply'
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={`${styles.commentActionBtn} ${styles.commentActionIconBtn}`}
                                                aria-label={
                                                    replies.length
                                                        ? `Reply (${replies.length})`
                                                        : 'Reply'
                                                }
                                                disabled={isBusy}
                                                onClick={() => {
                                                    cancelEditComment()
                                                    setReplyOpenId(
                                                        isReplyOpen
                                                            ? null
                                                            : item.id
                                                    )
                                                }}
                                            >
                                                <Icon
                                                    icon="mdi:reply-outline"
                                                    width={14}
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                    {canEditItem ? (
                                        <Tooltip
                                            title={
                                                item.isResolved
                                                    ? 'Reopen'
                                                    : 'Resolve'
                                            }
                                        >
                                            <button
                                                type="button"
                                                className={`${styles.commentActionBtn} ${styles.commentActionIconBtn}`}
                                                aria-label={
                                                    item.isResolved
                                                        ? 'Reopen'
                                                        : 'Resolve'
                                                }
                                                disabled={isBusy}
                                                onClick={() =>
                                                    handleToggleResolve(item)
                                                }
                                            >
                                                <Icon
                                                    icon={
                                                        item.isResolved
                                                            ? 'mdi:reload'
                                                            : 'mdi:check-circle-outline'
                                                    }
                                                    width={14}
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                    {canDeleteItem ? (
                                        <Tooltip title="Delete">
                                            <button
                                                type="button"
                                                className={`${styles.commentActionBtn} ${styles.commentActionIconBtn} ${styles.commentActionDanger}`}
                                                aria-label="Delete"
                                                disabled={isBusy}
                                                onClick={() =>
                                                    handleDeleteComment(item)
                                                }
                                            >
                                                <Icon
                                                    icon="mdi:trash-can-outline"
                                                    width={14}
                                                />
                                            </button>
                                        </Tooltip>
                                    ) : null}
                                </div>
                                ) : null}
                            </div>
                        )
                    })
                )}
            </div>
        </div>
    )

    const extraPanels = [
        {
            id: HOST_COMMENTS_TAB,
            label: (
                <>
                    Comments
                    <span className="pmx-tab-count">{comments.length}</span>
                </>
            ),
            content: commentsPanel,
        },
        // Versions tab hidden for now
        // {
        //     id: 'html-versions',
        //     label: (
        //         <>
        //             Versions
        //             <span className="pmx-tab-count">
        //                 {historyVersions.length}
        //             </span>
        //         </>
        //     ),
        //     content: htmlVersionsPanel,
        // },
    ]

    const editorTabToolbar = (
        <div className={styles.tabBarToolbar}>
            <button
                type="button"
                className={styles.tabBarBackButton}
                onClick={onBack}
                aria-label="Back to prototypes"
                title="Back to prototypes"
            >
                <Icon icon="mdi:arrow-left" width={16} />
            </button>
            <span
                className={styles.tabBarTitle}
                title={detail?.name || 'Prototype'}
            >
                {detail?.name || 'Prototype'}
            </span>
        </div>
    )

    if (loadError && !drawingId) {
        return (
            <div className={styles.errorState}>
                <div>{loadError || 'Unable to open drawing editor'}</div>
                <button type="button" className={styles.ghostButton} onClick={onBack}>
                    Back to list
                </button>
                <button
                    type="button"
                    className={styles.ghostButton}
                    onClick={() => load()}
                >
                    Retry
                </button>
            </div>
        )
    }

    return (
        <>
        {tabBarSlot ? createPortal(editorTabToolbar, tabBarSlot) : null}
        <input
            ref={versionInputRef}
            type="file"
            accept=".html,.htm,text/html"
            style={{ display: 'none' }}
            onChange={(e) => {
                const file = e.target.files?.[0]
                e.target.value = ''
                if (file) handleUploadVersion(file)
            }}
        />
        <div className={styles.editorPage}>
            {!editorSettled ? (
                <div className={styles.fullBleedCover}>
                    <Spin />
                    <span>Opening prototype…</span>
                </div>
            ) : null}
            <div
                className={`${styles.editorShell}${
                    fullWidth ? ` ${styles.editorShellFullWidth}` : ''
                }`}
            >
                <div
                    className={styles.editorHost}
                    ref={hostRef}
                >
                    {hostReady && drawingId ? (
                        <EditorErrorBoundary
                            onError={() => {
                                void clearDrawingIdForPrototype(
                                    prototypeId,
                                    detail?.currentVersionId || 'current'
                                )
                            }}
                            onRetry={() => {
                                remountEditor()
                                load()
                            }}
                        >
                            <div
                                className={`${styles.editorSurface} ${
                                    editorSettled
                                        ? styles.editorSurfaceReady
                                        : ''
                                }`}
                            >
                                <UserProvider user={editorUser}>
                                    <ToastProvider>
                                        <DrawingEditor
                                            ref={editorRef}
                                            key={drawingId}
                                            drawingId={drawingId}
                                            loadToken={editorMountKey}
                                            htmlIsolation="legacy"
                                            rightTab={rightTab}
                                            onRightTabChange={
                                                handleRightTabChange
                                            }
                                            commentsPanelProps={
                                                commentsPanelProps
                                            }
                                            historyPanelProps={
                                                historyPanelProps
                                            }
                                            extraPanels={extraPanels}
                                            onDrawingCreated={
                                                handleDrawingCreated
                                            }
                                            onMetaUpdated={markEditorSettled}
                                            onCommentsChange={
                                                handleCommentsChange
                                            }
                                            onSelectionChange={
                                                handleSelectionChange
                                            }
                                            onBeforeDelete={handleBeforeDelete}
                                            onAnnotationsRemoved={
                                                handleAnnotationsRemoved
                                            }
                                            onDirtyChange={handleDirtyChange}
                                            fullWidth={fullWidth}
                                            onToggleFullWidth={
                                                handleToggleFullWidth
                                            }
                                            showLeftSidebar={false}
                                            onAnnotationMoved={
                                                handleAnnotationMoved
                                            }
                                        />
                                        <SwitchUserModal />
                                        <ToastContainer />
                                    </ToastProvider>
                                </UserProvider>
                            </div>
                        </EditorErrorBoundary>
                    ) : null}
                </div>
            </div>
        </div>
        <DeletePopUp
            previewDelete={Boolean(deleteTarget)}
            loadingButton={deleteLoading}
            onCancel={closeDeletePopup}
            onDelete={confirmDeleteTarget}
            modalTitle={
                deleteTarget?.type === 'shape'
                    ? `Delete ${deleteTarget.shapeCount} Shape${
                          deleteTarget.shapeCount === 1 ? '' : 's'
                      }`
                    : deleteTarget?.type === 'reply'
                      ? 'Delete Reply'
                      : 'Delete Comment'
            }
            description={
                deleteTarget?.type === 'shape'
                    ? `${deleteTarget.commentCount} comment${
                          deleteTarget.commentCount === 1 ? ' is' : 's are'
                      } attached. Are you sure you want to delete?`
                    : undefined
            }
            pageKey={
                deleteTarget?.type === 'reply'
                    ? 'Reply'
                    : deleteTarget?.type === 'shape'
                      ? 'Shape'
                      : 'Comment'
            }
        />
        </>
    )
}

export default ProjectPrototypeDetailsView
